import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import BottomSheet from '../bottom_sheet/BottomSheet';
import { Label } from '../ui/label';
import { Combobox } from '../../shadcn/combobox/Combobox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { getAllClients } from '../../api/client';
import { addRoom, getRooms } from '../../api/room';
import { addSupplier, getSuppliers } from '../../api/supplier';
import { Checkbox } from '../ui/checkbox';
import TrashIcon from '../../assets/icons/trash.svg';
import ShareIcon from '../../assets/icons/share-icon.svg';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { eleminateCurrency, valuteNumberFormatter } from '../../helper/numberFormatter';
import WideSheetQuickAddClient from '../wide_sheet/WideSheetQuickAddClient';
import { getAll as getAllCategory } from '../../api/category';
import DefaultImageTest from '../../assets/images/default_avatar.png';
import { Config } from '../../utils';
import UploadIcon from '../../assets/icons/upload-icon.svg';
import {
  createPurchasedGoods,
  deletePurchasedGoods,
  getSinglePurchasedGood,
  updatePurchasedGoods,
} from '../../api/purchased-goods';
import { addDraperyOrderUpload, editDraperyOrderUpload } from '../../api/drapery-order-upload';
import ActionModal from '../../modals/action-modal';
import ConfirmModal from '../../modals/confirm_modal';
import { handleScrollToField } from '../../helper/scrollIntoView';
import { toast } from 'sonner';
import { openImagePreview, PdfPreview } from '../../helper/imageHelper';
import { mapStoredUploadsToRemoteItems, uploadFileWithPresign } from '../../helper/presignedUpload';
import UploadErrorBanner from '../UI/upload-error-banner';
import {
  ChevronDown,
  LockKeyhole,
  LucideCalendar,
  LucideCheck,
  LucideChevronDown,
  LucideExternalLink,
  LucideInfo,
  LucidePlus,
  LucideX,
} from 'lucide-react';
import { getAll as getAllItems } from '../../api/item';
import { getAll as getAllCategories } from '../../api/category';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../UI/calendar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip-shadcn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import { resizeTextarea } from '../../helper/elements';
import StandardItemModal from '../my-shop/standard-item-modal/main';
import {
  calculateComponentPrice,
  deepEqual,
  normalizeUrl,
  resolveItemBillingTypeClientBased,
} from '../../helper/helpers';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '../UI/alert-dialog';
import { decimalNumbersRegex } from '../../helper/regex';
import { trackView } from '../analytics/track_view';
import { MixpanelEvents } from '../analytics/mix_panel_events';

type FormProps = {
  client: any;
  room: any;
  itemType: any;
  supplier: any;
  notes: string;
  window_location_id: string;
};

type ComponentsTotalForm = {
  estimateDescription: string;
  qty: string;
  cost: string;
  markup: string;
  calculatedPrice: string;
  customerFacingPrice: string;
  shippingAndHandlingDescription: string;
  shippingAndHandlingQty: string;
  shippingAndHandlingCost: string;
  shippingAndHandlingMarkup: string;
  shippingAndHandlingCalculatedPrice: string;
  shippingAndHandlingCustomerFacingPrice: string;
};

type SaveMode = 'draft' | 'lock';
type SubmitOptions = {
  autoSave?: boolean;
};

const ALLOWED_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'svg', 'webp'];
const MAX_FILE_SIZE_KB = 10240;
const PDF_CONTENT_TYPE = 'application/pdf';
const PDF_UPLOAD_ERROR_MESSAGE = 'Failed to upload PDF. Please try again.';
const ESTIMATE_STATUS_OPTIONS = ['Not Created', 'Created', 'Exported to QB', 'Sent to Customer'];
const INVOICE_STATUS_OPTIONS = ['Not Created', 'Not Paid', 'Partial', 'Paid'];
const PO_STATUS_OPTIONS = [
  'Not Created',
  'Created',
  'Sent',
  'Acknowledged',
  'Partially Received',
  'Received',
  'Closed',
  'Canceled',
];
const KNOWN_ESTIMATE_STATUSES = new Set(ESTIMATE_STATUS_OPTIONS);

const resolvePurchasedGoodsSavedStatus = (saveMode: SaveMode) =>
  saveMode === 'lock' ? 'Price Locked' : 'Draft';

const resolveUploadsStatus = (uploads: any[]) => (uploads?.length > 0 ? 'yes' : 'no');

const buildUploadSaveSignature = (uploads: any[] = []) =>
  uploads.map((upload: any) => ({
    name: upload?.name || '',
    source: upload?.source || '',
    remoteSource: upload?.remoteSource || '',
    remoteKey: upload?.remoteKey || '',
    type: upload?.fileType || upload?.mimeType || upload?.type || '',
    size: upload?.size || 0,
    lastModified: upload?.lastModified || 0,
  }));

type PurchasedGoodsDocumentState = {
  estimateStatus: string;
  estimateNumber: string;
  estimateDateSent: Date | string | null;
  invoiceStatus: string;
  invoiceNumber: string;
  invoiceDateSent: Date | string | null;
  poStatus: string;
  poNumber: string;
  poDate: Date | string | null;
  primaryPoComponentId: any;
  primaryPoComponentSource: 'build' | 'total' | null;
};

const initialPurchasedGoodsDocumentState: PurchasedGoodsDocumentState = {
  estimateStatus: 'Not Created',
  estimateNumber: '',
  estimateDateSent: null,
  invoiceStatus: 'Not Created',
  invoiceNumber: '',
  invoiceDateSent: null,
  poStatus: 'Not Created',
  poNumber: '',
  poDate: null,
  primaryPoComponentId: null,
  primaryPoComponentSource: null,
};

const normalizeDocumentNumber = (value: any) =>
  String(value || '')
    .replace(/^#/, '')
    .trim();

const hasOwn = (obj: any, key: string) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const normalizeInvoiceStatus = (value: any) => {
  const normalized = String(value || '').trim();
  const lowered = normalized.toLowerCase();

  if (!normalized) return 'Not Created';
  if (lowered === 'yes') return 'Paid';
  if (lowered === 'no') return 'Not Paid';

  return normalized;
};

const normalizePoStatusValue = (value: any) =>
  String(value || '')
    .trim()
    .toLowerCase();

const isCanceledPoStatus = (value: any) => {
  const normalized = normalizePoStatusValue(value);
  return normalized === 'canceled' || normalized === 'cancelled';
};

// const isClearedPoStatus = (value: any) => {
//   const normalized = normalizePoStatusValue(value);
//   return normalized === 'not created' || isCanceledPoStatus(value);
// };

// const normalizePurchasedGoodsPoDocumentState = (
//   state: PurchasedGoodsDocumentState
// ): PurchasedGoodsDocumentState => {
//   if (!isClearedPoStatus(state.poStatus)) {
//     return state;
//   }

//   return {
//     ...state,
//     poNumber: state.poStatus === 'Not Created' ? '' : state.poNumber,
//     poDate: null,
//   };
// };

const dateFromUnknown = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return moment(value).isValid() ? value : undefined;
  return moment(value).isValid() ? moment(value).toDate() : undefined;
};

const formatHeaderDate = (value: any) => {
  const dateValue = dateFromUnknown(value);
  return dateValue ? moment(dateValue).format('MM/DD/YY') : '';
};

const getTimestamp = (value: any) => {
  const dateValue = dateFromUnknown(value);
  return dateValue ? dateValue.getTime() : 0;
};

const getPoStatusFromComponent = (component: any) => {
  const explicitStatus = component?.poStatus || component?.purchaseOrderStatus || '';
  if (explicitStatus) return explicitStatus;
  if (component?.receivedDate) return 'Received';
  if (component?.orderedDate || component?.poDate) return 'Sent';
  if (component?.po || component?.poNumber) return 'Created';
  return 'Not Created';
};

const collectPurchasedGoodsPoCandidates = (item: any) => {
  const buildCandidates = Array.isArray(item?.purchasedGoodsComponentsBuild)
    ? item.purchasedGoodsComponentsBuild.map((component: any) => ({
        ...component,
        primaryPoComponentSource: 'build' as const,
      }))
    : [];
  const totalCandidates = Array.isArray(item?.purchasedGoodsComponents)
    ? item.purchasedGoodsComponents.map((component: any, index: number) => ({
        ...component,
        id: component?.id || `total-${index}`,
        primaryPoComponentSource: 'total' as const,
      }))
    : [];

  let cands = [];

  if (item?.purchasedGoodsComponentsType === 'components-build') {
    cands = buildCandidates;
  } else {
    cands = totalCandidates;
  }

  return cands
    .filter((component: any) => {
      const status = getPoStatusFromComponent(component);
      if (isCanceledPoStatus(status)) return false;
      return Boolean(
        component?.po ||
        component?.poNumber ||
        component?.orderedDate ||
        component?.poDate ||
        component?.receivedDate ||
        component?.poStatus
      );
    })
    .sort((a: any, b: any) => {
      const aDate = getTimestamp(a?.updatedAt || a?.orderedDate || a?.poDate || a?.createdAt);
      const bDate = getTimestamp(b?.updatedAt || b?.orderedDate || b?.poDate || b?.createdAt);
      return bDate - aDate;
    });
};

const buildPurchasedGoodsDocumentState = (
  item: any,
  forceNotCreated = false
): PurchasedGoodsDocumentState => {
  if (!item || forceNotCreated) {
    return { ...initialPurchasedGoodsDocumentState };
  }

  const estimateJson = item?.draperyOrderSummary?.estimateJson || {};
  const invoiceJson = item?.draperyOrderSummary?.invoiceJson || {};
  const rawEstimateNumber = hasOwn(estimateJson, 'num')
    ? estimateJson?.num
    : (item?.estimateNumber ?? item?.qbEstimateNumber ?? '');
  const normalizedRawEstimateNumber = String(rawEstimateNumber || '').trim();
  const estimateNumber = KNOWN_ESTIMATE_STATUSES.has(normalizedRawEstimateNumber)
    ? ''
    : normalizeDocumentNumber(normalizedRawEstimateNumber);
  const estimateStatus =
    estimateJson?.status ||
    (KNOWN_ESTIMATE_STATUSES.has(normalizedRawEstimateNumber) ? normalizedRawEstimateNumber : '') ||
    item?.estimateStatus ||
    'Not Created';
  const estimateDateSent = hasOwn(estimateJson, 'dateSent')
    ? estimateJson?.dateSent || null
    : item?.estimateDateSent || item?.estimateDate || null;
  const invoiceStatus = normalizeInvoiceStatus(
    item?.invoicePaid || item?.invoiceStatus || invoiceJson?.paid || 'Not Created'
  );
  const poCandidates = collectPurchasedGoodsPoCandidates(item);
  const primaryPo = poCandidates[0] || null;

  return {
    estimateStatus,
    estimateNumber,
    estimateDateSent,
    invoiceStatus,
    invoiceNumber: normalizeDocumentNumber(
      item?.invoiceNumber || invoiceJson?.num || item?.invoiceNum || ''
    ),
    invoiceDateSent:
      item?.invoiceDateSent || item?.invoiceDate || invoiceJson?.invoiceDateSent || null,
    poStatus: primaryPo ? getPoStatusFromComponent(primaryPo) : 'Not Created',
    poNumber: normalizeDocumentNumber(primaryPo?.po || primaryPo?.poNumber || ''),
    poDate: primaryPo?.orderedDate || primaryPo?.poDate || null,
    primaryPoComponentId: primaryPo?.id || null,
    primaryPoComponentSource: primaryPo?.primaryPoComponentSource || null,
  };
};

const getEstimateStatusClass = (value: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (normalized === 'created') return 'estimate_status_created';
  if (normalized === 'exported to qb') return 'estimate_status_exported';
  if (normalized === 'sent to customer') return 'estimate_status_sent';
  return 'estimate_status_not_created';
};

const getInvoiceStatusClass = (value: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (normalized === 'paid' || normalized === 'yes') return 'invoicePaid_paid';
  if (normalized === 'partial') return 'invoicePaid_partial';
  if (normalized === 'not paid' || normalized === 'no') return 'invoicePaid_not_paid';
  return 'invoicePaid_not_created';
};

const getPoStatusClass = (value: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (normalized === 'received' || normalized === 'closed') return 'status_ready';
  if (normalized === 'canceled' || normalized === 'cancelled') return 'status_rejected';
  if (normalized === 'created' || normalized === 'sent' || normalized === 'acknowledged') {
    return 'status_ordered';
  }
  if (normalized === 'partially received') return 'status_components_arrived';
  return 'base_status_color';
};

type HeaderDocumentPillProps = {
  label: string;
  status: string;
  statusOptions: string[];
  statusClassName: (value: string) => string;
  number: string;
  date: Date | string | null;
  disabled?: boolean;
  dataQaPrefix: string;
  onStatusChange: (value: string) => void;
  onNumberChange: (value: string) => void;
  onDateChange: (value: Date | null) => void;
};

const HeaderDocumentPill = ({
  label,
  status,
  statusOptions,
  statusClassName,
  number,
  date,
  disabled = false,
  dataQaPrefix,
  onStatusChange,
  onNumberChange,
  onDateChange,
}: HeaderDocumentPillProps) => {
  const [statusOpen, setStatusOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [editingNumber, setEditingNumber] = useState(false);
  const [draftNumber, setDraftNumber] = useState(number || '');
  const formattedDate = formatHeaderDate(date);
  const selectedDate = dateFromUnknown(date);

  useEffect(() => {
    if (!editingNumber) {
      setDraftNumber(number || '');
    }
  }, [number, editingNumber]);

  const commitNumber = () => {
    onNumberChange(normalizeDocumentNumber(draftNumber));
    setEditingNumber(false);
  };

  return (
    <div
      className={`inline-flex min-h-[30px] items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm ${
        disabled ? 'opacity-70' : ''
      }`}
      data-qa={`${dataQaPrefix}_header_pill`}
    >
      <span className="text-muted-foreground">{label}</span>
      <Popover
        open={statusOpen}
        onOpenChange={(value) => {
          if (disabled) return;
          setStatusOpen(value);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-qa={`${dataQaPrefix}_status_trigger`}
            className={`inline-flex items-center gap-0.5 whitespace-nowrap font-medium disabled:cursor-not-allowed ${statusClassName(
              status
            )}`}
          >
            <span>{status || 'Not Created'}</span>
            {!disabled && <LucideChevronDown className="h-3 w-3" />}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[99999999] w-52 rounded-md border border-input bg-popover p-1 shadow-md"
        >
          <div className="flex max-h-[320px] flex-col gap-1 overflow-y-auto">
            {statusOptions.map((option) => (
              <button
                key={option}
                type="button"
                data-qa={`${dataQaPrefix}_status_item_${option.replace(/\s+/g, '_')}`}
                className={`flex items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent ${statusClassName(
                  option
                )}`}
                onClick={() => {
                  onStatusChange(option);
                  setStatusOpen(false);
                }}
              >
                <span>{option}</span>
                {option === status && <LucideCheck className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {editingNumber && !disabled ? (
        <Input
          data-qa={`${dataQaPrefix}_number_input`}
          value={draftNumber}
          onChange={(event) => setDraftNumber(event.currentTarget.value)}
          onBlur={commitNumber}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commitNumber();
            }
            if (event.key === 'Escape') {
              setDraftNumber(number || '');
              setEditingNumber(false);
            }
          }}
          autoFocus
          className="h-6 w-[88px] rounded-sm border-0 bg-muted px-1 text-xs shadow-none focus-visible:ring-1"
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          data-qa={`${dataQaPrefix}_number_trigger`}
          className="whitespace-nowrap text-muted-foreground hover:text-primary disabled:cursor-not-allowed"
          onClick={() => {
            if (disabled) return;
            setEditingNumber(true);
          }}
        >
          {number ? `#${number}` : '#_'}
        </button>
      )}

      <Popover
        open={dateOpen}
        onOpenChange={(value) => {
          if (disabled) return;
          setDateOpen(value);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-qa={`${dataQaPrefix}_date_trigger`}
            className="group/date inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground hover:text-primary disabled:cursor-not-allowed"
          >
            <LucideCalendar className="h-3.5 w-3.5" />
            <span>{formattedDate || 'MM/DD/YY'}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[99999999] w-auto rounded-md border border-input bg-popover p-0 shadow-md"
        >
          <Calendar
            data-qa={`${dataQaPrefix}_date_calendar`}
            mode="single"
            selected={selectedDate}
            className="rounded-md border-0 p-[12px]"
            onSelect={(value) => {
              onDateChange(value || null);
              setDateOpen(false);
            }}
          />
          {formattedDate && (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                onClick={() => {
                  onDateChange(null);
                  setDateOpen(false);
                }}
              >
                <LucideX className="h-3 w-3" />
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const PurchasedGoods = (props: any) => {
  const notebookRef = useRef<any>(null);
  const { purchasedGoodsPrefill } = props;

  const {
    onSave,
    onCancel,
    onDuplicate,
    onDelete,
    onAutoSave,
    open,
    existingId,
    prefilledClient,
    accountUser,
    canAccessTeamSettings,
    account,
  } = props;
  const accountId = parseFloat(localStorage.getItem('accountId') || '0');

  let initialBuildFromMyShopComponent = {
    id: crypto.randomUUID(),
    po: '',
    poGenerated: false,
    poStatus: 'Not Created',
    eta: null,
    qty: '1',
    cost: '',
    item: null,
    category: null,
    notes: '',
    poNotes: '',
    fabric: null,
    markup: '',
    headrail: null,
    quantity: '1',
    purchaser: 1,
    orderedDate: null,
    receivedDate: null,
    isStaticHardware: false,
    calculatedPrice: '',
    customerFacingPrice: '',
    clientPriceAdjustment: '',
    clientPriceAdjustmentType: '',
  };

  let initialBuildFromMyShopComponents = [initialBuildFromMyShopComponent];

  const initialForm: FormProps = {
    client: null,
    room: null,
    itemType: null,
    supplier: null,
    notes: '',
    window_location_id: '',
  };

  const initialComponentsTotalForm = {
    estimateDescription: '',
    qty: '1',
    cost: '',
    markup: '',
    calculatedPrice: '',
    customerFacingPrice: '',
    shippingAndHandlingDescription: 'Shipping & Handling',
    shippingAndHandlingQty: '1',
    shippingAndHandlingCost: '',
    shippingAndHandlingMarkup: '',
    shippingAndHandlingCalculatedPrice: '',
    shippingAndHandlingCustomerFacingPrice: '',
  };

  type ComponentsBuildShipping = {
    orderId: any;
    active: boolean;
    qty: string;
    name: string;
    cost: string;
    markup: string;
    price: string;
    customerFacingPrice: string;
    clientPriceAdjustment: string;
    clientPriceAdjustmentType: string;
  };

  let initialBuildShipping: ComponentsBuildShipping = {
    orderId: '',
    active: true,
    qty: '1',
    name: '',
    cost: '',
    markup: '',
    price: '',
    customerFacingPrice: '',
    clientPriceAdjustment: '',
    clientPriceAdjustmentType: '',
  };

  type ComponentsBuildPricing = {
    orderId: any;
    type: string;
    cost: string;
    markup: string;
    price: string;
    customerFacingPrice: string;
    clientPriceAdjustment: string;
    clientPriceAdjustmentType: string;
    customCost: string;
    customMarkup: string;
    customPrice: string;
    customCustomerFacingPrice: string;
    customClientPriceAdjustment: string;
    customClientPriceAdjustmentType: string;
    quantity: string;
    customQuantity: string;
  };

  let initialBuildPricing: ComponentsBuildPricing = {
    orderId: '',
    type: 'automatic',
    cost: '',
    markup: '',
    price: '',
    customerFacingPrice: '',
    clientPriceAdjustment: '',
    clientPriceAdjustmentType: '',
    customCost: '',
    customMarkup: '',
    customPrice: '',
    customCustomerFacingPrice: '',
    customClientPriceAdjustment: '',
    customClientPriceAdjustmentType: '',
    quantity: '1',
    customQuantity: '1',
  };

  type InitialEditMyShopItemType = {
    active: boolean;
    item: any;
    lockPrefill: boolean;
    prefill: any;
    componentId: any;
  };

  let initialEditMyShopItem: InitialEditMyShopItemType = {
    active: false,
    item: null,
    lockPrefill: false,
    prefill: null,
    componentId: null,
  };

  // REFS
  const confirmModalRef = useRef<any>(null);
  const buildShippingRef = useRef<any>(null);
  const buildPricingRef = useRef<any>(null);
  const shouldAutoRecalculateBuildPricingRef = useRef(true);
  const lockedAutoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedAutoSaveInFlightRef = useRef(false);
  const lockedAutoSaveSignatureRef = useRef<string>('');

  // FORM
  const [formErrors, setFormErrors] = useState<any>({
    client: {
      active: false,
      message: '',
    },
    room: {
      active: false,
      message: '',
    },
    itemType: {
      active: false,
      message: '',
    },
    supplier: {
      active: false,
      message: '',
    },
    notes: {
      active: false,
      message: '',
    },
  });
  const [buildFromMyShopComponents, setBuildFromMyShopComponents] = useState<any[]>(
    initialBuildFromMyShopComponents
  );
  const [buildFromMyShopComponentsCopy, setBuildFromMyShopComponentsCopy] = useState<any[]>(
    initialBuildFromMyShopComponents
  );
  const [existingUploadsId, setExistingUploadsId] = useState<any>(0);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [persistedPriceLocked, setPersistedPriceLocked] = useState<boolean>(false);
  const [unlockedForEditing, setUnlockedForEditing] = useState<boolean>(false);
  const [alternateSalesDescription, setAlternateSalesDescription] = useState<string>('');
  const [alternateSalesDescriptionCopy, setAlternateSalesDescriptionCopy] = useState<string>('');
  const [form, setForm] = useState<FormProps>(initialForm);
  const [formCopy, setFormCopy] = useState<FormProps>(initialForm);
  const [componentsTotalForm, setComponentsTotalForm] = useState<ComponentsTotalForm>(
    initialComponentsTotalForm
  );
  const [componentsTotalFormCopy, setComponentsTotalFormCopy] = useState<ComponentsTotalForm>(
    initialComponentsTotalForm
  );
  const [whoWillOrder, setWhoWillOrder] = useState<string>('workroom');
  const [whoWillOrderCopy, setWhoWillOrderCopy] = useState<string>('workroom');
  const [componentsType, setComponentsType] = useState<string>('components-build');
  const [pricingType, setPricingType] = useState<string>('automatic');
  const [buildFromMyShopComponentsShipping, setBuildFromMyShopComponentsShipping] =
    useState<ComponentsBuildShipping>(initialBuildShipping);
  const [buildFromMyShopComponentsShippingCopy, setBuildFromMyShopComponentsShippingCopy] =
    useState<ComponentsBuildShipping>(initialBuildShipping);
  const [buildFromMyShopComponentsPricing, setBuildFromMyShopComponentsPricing] =
    useState<ComponentsBuildPricing>(initialBuildPricing);
  const [buildFromMyShopComponentsPricingCopy, setBuildFromMyShopComponentsPricingCopy] =
    useState<ComponentsBuildPricing>(initialBuildPricing);
  const [pendingItemSupplierChange, setPendingItemSupplierChange] = useState<{
    key: string;
    item: any;
  }>({
    key: '',
    item: null,
  });
  // FETCH
  const [rooms, setRooms] = useState<any[]>([]);
  const [itemTypes, setItemTypes] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [myShopItems, setMyShopItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [flattenedClients, setFlattenedClients] = useState<any[]>([]);
  // const [clients, setClients] = useState<any[]>([]);
  // CONDITIONS
  const [shippingAndHandling, setShippingAndHandling] = useState<boolean>(false);
  const [wideSheetActive, setWideSheetActive] = useState<boolean>(false);
  const [refetchClient, setRefetchClient] = useState<boolean>(false);
  const [refetchRoom, setRefetchRoom] = useState<boolean>(false);
  const [refetchSuppliers, setRefetchSuppliers] = useState<boolean>(false);
  const [refetchItems, setRefetchItems] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState<boolean>(purchasedGoodsPrefill?.duplicate || false);
  const [openItemSupplierChange, setOpenItemSupplierChange] = useState<boolean>(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState<boolean>(false);
  const [componentRowErrors, setComponentRowErrors] = useState<Record<string, string>>({});
  // OTHER
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [imageUploads, setImageUploads] = useState<any[]>([]);
  // @ts-ignore
  const [imageUploadsCopy, setImageUploadsCopy] = useState<any[]>([]);
  const [documentState, setDocumentState] = useState<PurchasedGoodsDocumentState>(
    initialPurchasedGoodsDocumentState
  );
  const [documentStateCopy, setDocumentStateCopy] = useState<PurchasedGoodsDocumentState>(
    initialPurchasedGoodsDocumentState
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const pdfObjectUrlsRef = useRef<Record<string, string>>({});
  const [pdfObjectUrls, setPdfObjectUrls] = useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const nonNegativeQuantityRegex = /^\d*\.?\d*$/;
  const [clientBillingInfo, setClientBillingInfo] = useState<any>({
    additionalPriceAdjustment: null,
    additionalPriceAdjustmentValue: null,
  });
  const [editMyShopItem, setEditMyShopItem] =
    useState<InitialEditMyShopItemType>(initialEditMyShopItem);

  const isHardwareChosen = form?.itemType?.name?.toLowerCase()?.includes('hardware');
  // Client additional price adjustment
  const additionalPriceAdjustment = clientBillingInfo?.additionalPriceAdjustment || null;
  // Client additional price adjustment value
  const additionalPriceAdjustmentValue = clientBillingInfo?.additionalPriceAdjustmentValue || null;
  const canEditMyShop = account?.isOwner || account?.isAdmin || account?.canSeePricing;
  const canAccessPurchasedGoods = !!canEditMyShop;
  const hasPurchasedGoodsEditPermission = canAccessPurchasedGoods;
  const isPriceLockedView = !!persistedPriceLocked && !unlockedForEditing && !duplicate;
  const pricingFieldsDisabled =
    loading || disabled || !hasPurchasedGoodsEditPermission || isPriceLockedView;
  const nonPricingDisabled = loading || disabled || !hasPurchasedGoodsEditPermission;
  const primarySaveMode: SaveMode =
    existingId && persistedPriceLocked && !duplicate ? 'lock' : 'draft';
  const alternateSaveMode: SaveMode = primarySaveMode === 'draft' ? 'lock' : 'draft';
  const statusBadgeLabel = duplicate
    ? 'Copy Item'
    : !existingId
      ? 'New Item'
      : isPriceLockedView
        ? 'Price Locked'
        : 'Draft';

  const handleSetDuplicate = (val: boolean) => {
    setDuplicate(val);
  };

  const getComponentKey = (component: any, index?: number) =>
    String(component?.id ?? component?.item?.id ?? index ?? '');

  const getLiveMyShopItem = (item: any) => {
    if (!item?.id) return null;
    return myShopItems.find((myShopItem: any) => myShopItem?.id === item.id) || null;
  };

  const replaceComponentSnapshotWithLiveItem = (component: any) => {
    const liveItem = getLiveMyShopItem(component?.item);
    if (!liveItem) return component;

    return {
      ...component,
      item: liveItem,
      cost: liveItem?.cost || component?.cost || '',
      markup: '',
      calculatedPrice: '',
      customerFacingPrice: '',
      clientPriceAdjustment: '',
      clientPriceAdjustmentType: '',
    };
  };

  const refreshComponentsFromLiveItems = (syncCopy = false) => {
    if (!myShopItems?.length) return;
    setBuildFromMyShopComponents((prev) => prev.map(replaceComponentSnapshotWithLiveItem));
    if (syncCopy) {
      setBuildFromMyShopComponentsCopy((prev) => prev.map(replaceComponentSnapshotWithLiveItem));
    }
  };

  const validateActiveBuildComponents = () => {
    if (componentsType !== 'components-build' || !myShopItems?.length) {
      setComponentRowErrors({});
      return true;
    }

    const activeItemIds = new Set(myShopItems.map((item: any) => item?.id).filter(Boolean));
    const nextErrors: Record<string, string> = {};

    buildFromMyShopComponents.forEach((component: any, index: number) => {
      if (component?.item?.id && !activeItemIds.has(component.item.id)) {
        nextErrors[getComponentKey(component, index)] =
          'This component no longer exists in My Shop.';
      }
    });

    setComponentRowErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.error('Component no longer exists', {
        description: 'This component no longer exists in My Shop.',
        duration: 3000,
      });
      return false;
    }

    return true;
  };

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleRefetchClient = () => {
    setRefetchClient((prev) => !prev);
  };

  const handleRefetchRoom = () => {
    setRefetchRoom((prev) => !prev);
  };

  const handleRefetchSuppliers = () => {
    setRefetchSuppliers((prev) => !prev);
  };

  const handleShowWideSheet = (val: boolean) => {
    setWideSheetActive(val);
  };

  const mapBuildShippingStateForForm = (shipping: any): ComponentsBuildShipping => {
    if (!shipping) return initialBuildShipping;

    return {
      ...initialBuildShipping,
      ...shipping,
      cost: shipping?.cost ? `$${shipping.cost}` : '',
      price: shipping?.price ? `$${shipping.price}` : '',
      customerFacingPrice: shipping?.customerFacingPrice
        ? `$${shipping.customerFacingPrice}`
        : shipping?.price
          ? `$${shipping.price}`
          : '',
    };
  };

  const handleComponentsTotalInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isPriceLockedView) return;

      let { name, value } = e.target;

      const numberNames = [
        'cost',
        'markup',
        'customerFacingPrice',
        'shippingAndHandlingCost',
        'shippingAndHandlingMarkup',
        'shippingAndHandlingCustomerFacingPrice',
      ];

      const calculateNames = ['cost', 'markup', 'qty'];
      const calculateShipmentNames = ['shippingAndHandlingCost', 'shippingAndHandlingMarkup'];

      if (name === 'qty' && value && !nonNegativeQuantityRegex.test(value)) return;
      if (numberNames.includes(name) && value && !decimalNumbersRegex.test(value)) {
        return;
      }

      setComponentsTotalForm((prev) => {
        const newForm = { ...prev, [name]: value };
        const markup_or_discount_parsed = Number(additionalPriceAdjustmentValue) || 0;
        const markup_or_discount =
          additionalPriceAdjustment === 'discount'
            ? -markup_or_discount_parsed
            : markup_or_discount_parsed;

        if (calculateNames.includes(name)) {
          const parsedQty = Number(eleminateCurrency(newForm.qty) || 0);
          const parsedCost = Number(eleminateCurrency(newForm.cost) || 0);
          const parsedMarkup = Number(eleminateCurrency(newForm.markup) || 0);
          const totalPercentage = 1 + parsedMarkup / 100 + markup_or_discount / 100;
          const parsedTotalCustomerFacingPrice = parsedQty * (parsedCost * totalPercentage);

          return {
            ...newForm,
            customerFacingPrice: `$${parsedTotalCustomerFacingPrice.toFixed(2)}`,
            calculatedPrice: parsedTotalCustomerFacingPrice.toFixed(2),
          };
        } else if (calculateShipmentNames.includes(name)) {
          const parsedShippmentQty = 1;
          const parsedShippingAndHandlingCost = Number(
            eleminateCurrency(newForm.shippingAndHandlingCost) || 0
          );
          const parsedShippingAndHandlingMarkup = Number(
            eleminateCurrency(newForm.shippingAndHandlingMarkup) || 0
          );
          const totalPercentageSH =
            1 + parsedShippingAndHandlingMarkup / 100 + markup_or_discount / 100;
          const parsedShippingAndHandlingCustomerFacingPrice =
            parsedShippmentQty * (parsedShippingAndHandlingCost * totalPercentageSH);

          return {
            ...newForm,
            shippingAndHandlingCustomerFacingPrice: `$${parsedShippingAndHandlingCustomerFacingPrice.toFixed(
              2
            )}`,
            shippingAndHandlingCalculatedPrice:
              parsedShippingAndHandlingCustomerFacingPrice.toFixed(2),
          };
        }

        return newForm;
      });
    },
    [additionalPriceAdjustmentValue, additionalPriceAdjustment, isPriceLockedView]
  );

  const handleComponentsTotalInputBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (isPriceLockedView) return;

    let { name, value } = e.target;
    const numberNames = [
      'cost',
      'markup',
      'customerFacingPrice',
      'shippingAndHandlingCost',
      'shippingAndHandlingMarkup',
      'shippingAndHandlingCustomerFacingPrice',
    ];

    const percentageNames = ['markup', 'shippingAndHandlingMarkup'];

    if (numberNames.includes(name) && value) {
      if (value.endsWith('.')) {
        value += '00';
      } else if (value?.split('.')?.[1]?.length === 1) {
        value += '0';
      } else if (!value.includes('.')) {
        value += '.00';
      }

      setComponentsTotalForm((prev) => {
        return {
          ...prev,
          [name]: percentageNames.includes(name) ? value : `$${value}`,
        };
      });
    }
  };

  const handleComponentsTotalInputFocus = (e: ChangeEvent<HTMLInputElement>) => {
    if (isPriceLockedView) return;

    let { name, value } = e.target;

    if (value && value.includes('$')) {
      setComponentsTotalForm((prev) => ({ ...prev, [name]: eleminateCurrency(value) }));
    }
  };

  const handleSelectShippingAndHandling = (val: boolean) => {
    if (isPriceLockedView) return;
    setShippingAndHandling(val);
  };

  const handleSelectShippingAndHandlingBuild = (val: boolean) => {
    if (isPriceLockedView) return;
    setBuildFromMyShopComponentsShipping((prev) => ({ ...prev, active: val }));
  };

  const handleSetShippingBuild = (e: any) => {
    if (isPriceLockedView) return;

    const { name, value } = e.target;

    if (name !== 'name' && value && !decimalNumbersRegex.test(value)) return;

    setBuildFromMyShopComponentsShipping((prev) => {
      let temp_additionalPriceAdjustment = isLocked
        ? prev.clientPriceAdjustmentType || ''
        : additionalPriceAdjustment || '';
      let temp_additionalPriceAdjustmentValue = isLocked
        ? prev.clientPriceAdjustment || ''
        : additionalPriceAdjustmentValue || '';

      const markup_or_discount_parsed = Number(temp_additionalPriceAdjustmentValue) || 0;
      const markup_or_discount =
        temp_additionalPriceAdjustment === 'discount'
          ? -markup_or_discount_parsed
          : markup_or_discount_parsed;

      let newPrice = '';

      if (!['name', 'active'].includes(name)) {
        let nVal = Number(value) || 0;
        let prevCostN = Number(eleminateCurrency(prev.cost)) || 0;
        let prevMarkupN = Number(prev.markup) || 0;

        if (name === 'cost') {
          newPrice = ((1 + prevMarkupN / 100 + markup_or_discount / 100) * nVal).toString();
        } else if (name === 'markup') {
          newPrice = ((1 + nVal / 100 + markup_or_discount / 100) * prevCostN).toString();
        } else if (name === 'customerFacingPrice') {
          newPrice = (Number(eleminateCurrency(value)) || 0).toString();
        }
      }

      if (Number(newPrice) <= 0) {
        newPrice = '';
      }

      const next = { ...prev, [name]: value };
      if (name === 'cost' || name === 'markup') {
        next.price = newPrice;
        next.customerFacingPrice = newPrice;
        return next;
      }
      if (name === 'customerFacingPrice') {
        next.price = newPrice;
        return next;
      }
      next.price = newPrice;
      return next;
    });
  };

  const handleChangeEnterTotalPricing = (e: ChangeEvent<HTMLInputElement>) => {
    if (isPriceLockedView) return;

    const { name, value } = e.target;

    if (value && !decimalNumbersRegex.test(value)) return;

    if (name === 'customerFacingPrice') {
      setBuildFromMyShopComponentsPricing((prev) => ({ ...prev, [name]: value }));
      return;
    }

    setBuildFromMyShopComponentsPricing((prev) => {
      let new_form = { ...prev, [name]: value };

      if (name !== 'customCustomerFacingPrice') {
        let nVal = Number(value) || 0;
        let customQuantity =
          name === 'customQuantity' ? nVal || 1 : Number(prev?.customQuantity) || 1;
        let customCost =
          name === 'customCost' ? nVal : Number(eleminateCurrency(prev?.customCost)) || 0;
        let customMarkup = name === 'customMarkup' ? nVal : Number(prev?.customMarkup) || 0;
        let temp_adj_type = isLocked
          ? prev?.customClientPriceAdjustmentType
          : additionalPriceAdjustment;
        let temp_adj_val = isLocked
          ? Number(prev?.customClientPriceAdjustment) || 0
          : Number(additionalPriceAdjustmentValue) || 0;

        const markup_or_discount = temp_adj_type === 'discount' ? -temp_adj_val : temp_adj_val;

        let final_markup = 1 + customMarkup / 100 + markup_or_discount / 100;
        let pre_final_price = customQuantity * customCost * final_markup;
        let customCustomerFacingPrice = pre_final_price ? pre_final_price.toFixed(2) : '';

        new_form['customCustomerFacingPrice'] = `$${customCustomerFacingPrice}`;
      }

      return new_form;
    });
  };

  const handleOnFocusInputPrice = (e: ChangeEvent<HTMLInputElement>, type: string, row?: any) => {
    const { name, value } = e.target;

    if (value && value.includes('$')) {
      switch (type) {
        case 'pricing':
          setBuildFromMyShopComponentsPricing((prev) => ({
            ...prev,
            [name]: eleminateCurrency(value),
          }));
          break;
        case 'shipping':
          setBuildFromMyShopComponentsShipping((prev) => ({
            ...prev,
            [name]: eleminateCurrency(value),
          }));
          break;
        case 'components':
          setBuildFromMyShopComponents((prev) => {
            return prev?.map((i) => {
              if (row && row?.id === i?.id) {
                return {
                  ...i,
                  [name]: eleminateCurrency(value),
                };
              }
              return i;
            });
          });
          break;
        default:
          break;
      }
    }
  };

  const handleOnBlurInputPrice = (e: ChangeEvent<HTMLInputElement>, type: string, row?: any) => {
    let { name, value } = e.target;

    if (value) {
      if (value.endsWith('.')) {
        value += '00';
      } else if (value?.split('.')?.[1]?.length === 1) {
        value += '0';
      } else if (!value.includes('.')) {
        value += '.00';
      }

      const isMarkup = name?.toLowerCase()?.includes('markup');

      switch (type) {
        case 'pricing':
          setBuildFromMyShopComponentsPricing((prev) => ({
            ...prev,
            [name]: isMarkup ? value : `$${value}`,
          }));
          break;
        case 'shipping':
          setBuildFromMyShopComponentsShipping((prev) => ({
            ...prev,
            [name]: isMarkup ? value : `$${value}`,
          }));
          break;
        case 'components':
          setBuildFromMyShopComponents((prev) => {
            return prev?.map((i) => {
              if (row && row?.id === i?.id) {
                return {
                  ...i,
                  [name]: isMarkup ? value : `$${value}`,
                };
              }
              return i;
            });
          });
          break;
        default:
          break;
      }
    }
  };

  const handleResetForm = () => {
    buildShippingRef.current = null;
    buildPricingRef.current = null;

    setClientBillingInfo({
      additionalPriceAdjustment: null,
      additionalPriceAdjustmentValue: null,
    });
    setFormErrors({
      client: {
        active: false,
        message: '',
      },
      room: {
        active: false,
        message: '',
      },
      itemType: {
        active: false,
        message: '',
      },
      supplier: {
        active: false,
        message: '',
      },
      notes: {
        active: false,
        message: '',
      },
    });
    setForm(initialForm);
    setFormCopy(initialForm);
    setDocumentState(initialPurchasedGoodsDocumentState);
    setDocumentStateCopy(initialPurchasedGoodsDocumentState);
    setWhoWillOrder('workroom');
    setWhoWillOrderCopy('workroom');
    setComponentsTotalForm(initialComponentsTotalForm);
    setComponentsTotalFormCopy(initialComponentsTotalForm);
    setComponentsType('components-build');
    setShippingAndHandling(false);
    setImageUploads([]);
    setImageUploadsCopy([]);
    setEditMyShopItem(initialEditMyShopItem);
    setBuildFromMyShopComponents(initialBuildFromMyShopComponents);
    setBuildFromMyShopComponentsCopy(initialBuildFromMyShopComponents);
    setAlternateSalesDescription('');
    setAlternateSalesDescriptionCopy('');
    setIsLocked(false);
    setPersistedPriceLocked(false);
    setUnlockedForEditing(false);
    setUnlockDialogOpen(false);
    setComponentRowErrors({});
    setBuildFromMyShopComponentsShipping(initialBuildShipping);
    setBuildFromMyShopComponentsShippingCopy(initialBuildShipping);
    setBuildFromMyShopComponentsPricing(initialBuildPricing);
    setBuildFromMyShopComponentsPricingCopy(initialBuildPricing);
    shouldAutoRecalculateBuildPricingRef.current = true;
    setOpenItemSupplierChange(false);
    setPendingItemSupplierChange({ key: '', item: null });
    setPricingType('automatic');
  };

  const handleOpenConfirmModal = (type: string = 'category-supplier-change') => {
    let check_case = false;
    switch (type) {
      case 'category-supplier-change':
        check_case = buildFromMyShopComponents?.some((i) => {
          return i?.item !== null || i?.category !== null;
        });
        setOpenItemSupplierChange(check_case);
        return check_case;
      default:
        return check_case;
    }
  };

  const handleResetComponents = () => {
    if (isPriceLockedView) return;
    handleOnChooseItem(pendingItemSupplierChange.key, pendingItemSupplierChange.item);
    setBuildFromMyShopComponents(initialBuildFromMyShopComponents);
    setOpenItemSupplierChange(false);
  };

  const handleOnChooseItem = (key: string, item: any) => {
    if (!key) return;
    if (isPriceLockedView) return;

    if (
      (key === 'itemType' || key === 'supplier') &&
      !pendingItemSupplierChange.key &&
      !pendingItemSupplierChange.item &&
      handleOpenConfirmModal()
    ) {
      setPendingItemSupplierChange({ key, item });
      return;
    }
    setTimeout(() => {
      setPendingItemSupplierChange({ key: '', item: null });
    }, 300);

    setFormErrors((prev: any) => ({ ...prev, [key]: { active: false, message: '' } }));

    if (key === 'client') {
      setRooms([]);
    }

    setForm((prev) => {
      const newForm = { ...prev, [key]: prev[key]?.id === item?.id ? null : item };

      if (key === 'client') {
        newForm.room = null;
      }

      return newForm;
    });
  };

  const handleOnChangeInput = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let { name, value } = e.target;
    if (isPriceLockedView && name !== 'notes') return;

    setFormErrors((prev) => ({ ...prev, [name]: { active: false, message: '' } }));

    setForm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleDocumentFieldChange = (
    field: keyof PurchasedGoodsDocumentState,
    value: Date | string | null
  ) => {
    setDocumentState((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      // if (field === 'poStatus') {
      //   return normalizePurchasedGoodsPoDocumentState(next);
      // }

      return next;
    });
  };

  const handleOnChangeBuiltComponents = (payload: any) => {
    const { name, value, item } = payload;
    if (!name) return;
    if (isPriceLockedView && name !== 'poNotes') return;

    if ((name === 'quantity' || name === 'qty') && value && !nonNegativeQuantityRegex.test(value)) {
      return;
    }

    if (name === 'cost' && value && !decimalNumbersRegex.test(value)) {
      return;
    }

    if (['qty', 'quantity', 'cost'].includes(name)) {
      shouldAutoRecalculateBuildPricingRef.current = true;
    }

    setBuildFromMyShopComponents((prev) => {
      return prev.map((i) => {
        let tempState = { ...i, [name]: value };

        if (name === 'quantity') {
          tempState['qty'] = value;
        }

        if (i.id === item.id) {
          return tempState;
        }
        return i;
      });
    });
  };

  const getCategories = async (activeOnly: boolean) => {
    try {
      const catRes = await getAllCategories({
        isActiveOnly: activeOnly || undefined,
      });
      setCategories(catRes);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFillSubcategories = () => {
    let catId = form?.itemType?.id;
    if (!catId) return;

    let subcategories = categories?.filter((c) => c.isActive && c?.parentCategoryId === catId);
    setSubCategories(subcategories);
  };

  useEffect(() => {
    handleFillSubcategories();

    return () => {};
  }, [categories, form.itemType]);

  const loadMyShopItems = async () => {
    try {
      const response = await getAllItems({
        isActiveOnly: true,
        includeDetails: true,
        excludeFees: true,
      });
      setMyShopItems(response);
    } catch (error) {
      console.error(error);
    }
  };

  const loadSuppliers = async () => {
    const params = {
      accountId,
      isActiveOnly: true,
      includeInactive: false,
    };
    const response = await getSuppliers(params);
    setSuppliers(response);
  };

  const loadItemType = async () => {
    const params = {
      term: '',
      isActiveOnly: true,
      isParentOnly: true,
    };
    const response = await getAllCategory(params);
    const disincludeNames = ['Fabrication', 'Fees'];
    const filtered_array = response?.filter((f) => !disincludeNames.includes(f.name));
    setItemTypes(filtered_array);
  };

  const loadRooms = async () => {
    if (form?.client) {
      const response = await getRooms(form?.client.id);
      setRooms(response);
    }
  };

  const loadClients = async (): Promise<void> => {
    let res: any;
    if (accountId) {
      res = await getAllClients({
        accountId,
        sort: { column: 'displayName', value: 'ASC' },
        includeInactive: true,
      });
    }

    if (res && !res.error) {
      const convertToSortTableRowInfo = (client: any) => {
        const rowInfo: any = client;
        if (client.subClients) {
          rowInfo.subList = client.subClients.map(convertToSortTableRowInfo);
        }
        return rowInfo;
      };

      let flattened_clients_function = (clients: any) => {
        let result: any = [];

        const traverse = (clientList: any) => {
          for (const c of clientList) {
            result.push(c);
            if (c.subClients && c.subClients.length > 0) {
              traverse(c.subClients);
            }
          }
        };

        traverse(clients);

        return result;
      };

      setFlattenedClients(flattened_clients_function(res));

      // const clients = res.map(convertToSortTableRowInfo);
      // const allSubClients = clients.reduce(
      //   (acc: any, client: any) => acc.concat(client.subList || []),
      //   [] as any[]
      // );

      // const updatedClients = [...clients, ...allSubClients];

      // setClients(updatedClients);
    }
  };

  const handleSetClient = (client: any) => {
    if (!client) return;
    setForm((prev) => ({ ...prev, client: client }));
    setFormErrors((prev) => ({ ...prev, client: { active: false, message: '' } }));
  };

  const handleCreateSupplier = async (value: any) => {
    if (!value) {
      setFormErrors((prev) => ({
        ...prev,
        supplier: { active: true, message: 'Enter Supplier name' },
      }));
      handleScrollToField('supplier');
      return;
    }

    const payload = {
      id: 0,
      active: true,
      avatarId: '',
      accountNumber: '',
      mainPhone: '',
      displayName: value,
      companyName: '',
      fax: '',
      notes: '',
      tags: '',
      type: 'installer',
      shippingAddress: {
        id: 0,
        accountNumber: '',
        cellPhone: '',
        cityTown: '',
        company: '',
        country: '',
        email: '',
        fax: '',
        firstName: '',
        homePhone: '',
        lastName: '',
        mainPhone: '',
        officePhone: '',
        paymentTerms: '',
        relation: '',
        stateProvince: '',
        streetAddress: '',
        unitSuite: '',
        website: '',
        zipCode: '',
      },
      billingAddress: {
        id: 0,
        accountNumber: '',
        cellPhone: '',
        cityTown: '',
        company: '',
        country: '',
        email: '',
        fax: '',
        firstName: '',
        homePhone: '',
        lastName: '',
        mainPhone: '',
        officePhone: '',
        paymentTerms: '',
        relation: '',
        stateProvince: '',
        streetAddress: '',
        unitSuite: '',
        website: '',
        zipCode: '',
      },
      additionalContacts: [],
      customSupplier: true,
      isCustom: false,
      accountId: accountId,
    };

    const response = await addSupplier(payload);
    if (response) {
      setForm((prev) => ({ ...prev, supplier: response }));
      setFormErrors((prev) => ({
        ...prev,
        supplier: { active: false, message: '' },
      }));
      handleRefetchSuppliers();
    }
  };

  const handleCreateNewRoom = async (value: any) => {
    if (!form.client) {
      setFormErrors((prev) => ({
        ...prev,
        client: { active: true, message: 'Client is required' },
      }));
      toast.error('Required Fields Missing', {
        description: 'Please fill in all required fields marked with an asterisk (*)',
      });
      handleScrollToField('client');
      return;
    }

    setFormErrors((prev) => ({ ...prev, client: { active: false, message: '' } }));

    const response = await addRoom({
      accountId,
      name: value,
      clientId: form?.client?.id,
    });
    if (response) {
      const { client, ...rest } = response;
      setForm((prev) => ({ ...prev, room: rest }));
      setFormErrors((prev) => ({ ...prev, room: { active: false, message: '' } }));
      handleRefetchRoom();
    }
  };

  const resolvePdfDisplayUrl = (uploadItem: any, fallbackUrl: string | null) => {
    if (!uploadItem || !isPdfUpload(uploadItem)) {
      return fallbackUrl;
    }
    if (uploadItem.fromRest) {
      return fallbackUrl;
    }
    const source = uploadItem.source;
    if (typeof source === 'string' && pdfObjectUrls[source]) {
      return pdfObjectUrls[source];
    }
    return fallbackUrl;
  };

  const isPdfUpload = (upload: any): boolean => {
    if (!upload) {
      return false;
    }

    const type = (upload.type || upload.mimeType || '').toLowerCase();
    const declaredType = typeof upload.fileType === 'string' ? upload.fileType.toLowerCase() : '';
    const name = (upload.name || upload.originalName || '').toLowerCase();
    const sourceString = typeof upload.source === 'string' ? upload.source : undefined;
    const normalizedSource = sourceString ? sourceString.toLowerCase() : '';
    const base64Segment = normalizedSource.includes('base64,')
      ? normalizedSource.split('base64,')[1]
      : '';
    const dataUriLooksPdf =
      !!sourceString &&
      (normalizedSource.startsWith('data:application/pdf') ||
        (normalizedSource.startsWith('data:application/octet-stream') &&
          !!base64Segment &&
          base64Segment.startsWith('jvber')));

    return (
      upload.isPdf === true ||
      type === 'pdf' ||
      declaredType === 'pdf' ||
      type === 'application/pdf' ||
      type.endsWith('+pdf') ||
      dataUriLooksPdf ||
      (!!name && name.endsWith('.pdf')) ||
      (!!normalizedSource && normalizedSource.includes('.pdf'))
    );
  };

  const convertToPng = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject('Canvas context is not supported.');
          return;
        }

        canvas.width = image.width;
        canvas.height = image.height;

        ctx.drawImage(image, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject('Conversion to PNG failed.');
              return;
            }

            const pngFile = new File([blob], file.name.replace(/\.\w+$/, '.png'));

            const pngUrl = URL.createObjectURL(pngFile);

            resolve(pngUrl);
          },
          'image/png',
          1
        );
      };

      image.onerror = () => {
        reject('Image loading failed.');
      };

      image.src = URL.createObjectURL(file);
    });
  };

  const handleFile = async (files: File[] | FileList, element?: string) => {
    if (!files || files.length === 0) {
      return;
    }

    setUploadError(null);

    const pendingImageUploads: any[] = [];
    const directUploads: any[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = (file.name || '').toLowerCase();
      const extension = fileName.split('.').pop() || '';
      const mimeType = (file.type || '').toLowerCase();
      const isPdf = extension === 'pdf' || mimeType === 'application/pdf';
      const isImageExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));

      if (!isPdf && !isImageExtension) {
        setUploadError('Valid file types: JPEG, PNG, SVG, WEBP, PDF.');
        continue;
      }

      if (file.size / 1024 > MAX_FILE_SIZE_KB) {
        setUploadError('Max allowed size is 10MB.');
        continue;
      }

      if (isPdf) {
        const upload: any = file;
        upload.source = URL.createObjectURL(file);
        upload.description = '';
        upload.fromRest = false;
        upload.isPdf = true;
        upload.blobToSend = file;
        upload.fileType = 'pdf';
        upload.mimeType = file.type || PDF_CONTENT_TYPE;

        if (!Config.fileStoreUrl.includes('localhost')) {
          try {
            const remoteInfo = await uploadFileWithPresign({
              accountId,
              contentType: file.type || PDF_CONTENT_TYPE,
              file,
              fileName: file.name || 'upload.pdf',
              uploadType: 'pdf',
            });
            upload.remoteKey = remoteInfo.remoteKey;
            upload.remoteFileName = remoteInfo.remoteFileName;
            upload.remoteSource = remoteInfo.remoteSource;
            upload.remoteUploaded = true;
          } catch (error) {
            console.error('Presigned PDF upload failed:', error);
            setUploadError(PDF_UPLOAD_ERROR_MESSAGE);
            continue;
          }
        }

        directUploads.push(upload);
        continue;
      }

      // payload for images
      let obj: any = {};

      if (fileName.endsWith('.svg') || fileName.endsWith('.webp')) {
        try {
          const pngUrl = await convertToPng(file);

          obj = file;
          obj.source = pngUrl;
          obj.description = '';
          obj.fromRest = false;
          obj.fileType = 'image';
          obj.mimeType = file.type;
          obj.blobToSend = file;
        } catch (error) {
          console.error(error);
        }
      } else {
        obj = file;
        obj.source = URL.createObjectURL(file);
        obj.description = '';
        obj.fromRest = false;
        obj.fileType = 'image';
        obj.mimeType = file.type;
        obj.blobToSend = file;
      }

      if (Object.keys(obj).length > 0) {
        pendingImageUploads.push(obj);
      }
    }

    // if (directUploads.length > 0) {
    //   setImageUploads(directUploads);
    // }
    setImageUploads((prev) => [...prev, ...directUploads, ...pendingImageUploads]);
  };

  // drag over upload
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  // drag leave upload
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  // drag and drop upload
  const handleDragDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);

    const files = e.dataTransfer.files;

    handleFile(files, 'drag_drop');
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || [];

    handleFile(files, 'choose_from_computer');
  };

  const handleChooseFile = () => {
    const inputEl = document.getElementById('fileInput');
    if (inputEl) {
      inputEl.click();
    }
  };

  const createPdfObjectUrlFromDataUri = (dataUri: string): string | null => {
    try {
      const base64Index = dataUri.indexOf('base64,');
      if (base64Index === -1) {
        return null;
      }
      const base64Data = dataUri.substring(base64Index + 7);
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to create PDF object URL', error);
      return null;
    }
  };

  const handleDeleteUploadedImage = (item: any) => {
    const filtered = imageUploads?.filter((i) => i.name !== item?.name);
    setImageUploads(filtered);
  };

  const handleCancel = () => {
    // uncomment if needs to be only for existing purchased good
    // if (existingId) {
    const is_deep_equal = deepEqual(
      {
        ...form,
        ...componentsTotalForm,
        whoWillOrder: whoWillOrder,
        alternateSalesDescription: alternateSalesDescription,
        buildFromMyShopComponents: buildFromMyShopComponents,
        buildFromMyShopComponentsShipping: buildFromMyShopComponentsShipping,
        buildFromMyShopComponentsPricing: buildFromMyShopComponentsPricing,
        documentState: documentState,
      },
      {
        ...formCopy,
        ...componentsTotalFormCopy,
        whoWillOrder: whoWillOrderCopy,
        alternateSalesDescription: alternateSalesDescriptionCopy,
        buildFromMyShopComponents: buildFromMyShopComponentsCopy,
        buildFromMyShopComponentsShipping: buildFromMyShopComponentsShippingCopy,
        buildFromMyShopComponentsPricing: buildFromMyShopComponentsPricingCopy,
        documentState: documentStateCopy,
      }
    );

    if (!is_deep_equal || imageUploads.length !== imageUploadsCopy.length) {
      setIsChanged(true);
      return;
    }
    // }

    handleResetForm();
    handleSetDuplicate(false);
    if (onCancel) onCancel();
  };

  const handleComponentsBuildRows = ({ type, payload }: { type: string; payload?: any }) => {
    if (isPriceLockedView) return;

    switch (type) {
      case 'create':
        setBuildFromMyShopComponents((prev: any) => {
          return [...prev, { ...initialBuildFromMyShopComponent, id: crypto.randomUUID() }];
        });
        return;
      case 'delete':
        shouldAutoRecalculateBuildPricingRef.current = true;

        setBuildFromMyShopComponents((prev: any) => {
          if (prev.length <= 1) {
            return [initialBuildFromMyShopComponent];
          }
          return prev.filter((f: any) => f.id !== payload?.id);
        });
        return;
      default:
        return;
    }
  };

  const handleSelectBuildItem = ({
    type,
    key = '',
    component,
    item = null,
  }: {
    type: string;
    key: 'category' | 'item' | '';
    component?: any;
    item?: any;
  }) => {
    if (!component || !type || !key) return;
    if (isPriceLockedView) return;

    switch (type) {
      case 'components-build':
        shouldAutoRecalculateBuildPricingRef.current = true;

        setBuildFromMyShopComponents((prev: any) => {
          return prev.map((i: any) => {
            if (i.id === component.id) {
              const exist = prev.some(
                (i: any) => i?.id === component.id && i?.[key]?.id === item.id
              );

              let new_form = { ...i, cost: '', [key]: exist ? null : item };

              if (key === 'item') {
                let foundSubCategory = subCategories?.find((i) => i?.id === item?.subcategoryId);
                new_form['category'] = foundSubCategory || null;
              } else if (key === 'category') {
                new_form['item'] = null;
              }

              return new_form;
            }
            return i;
          });
        });
        return;
      default:
        return;
    }
  };

  const handleOpenMyShopItemModal = (myShopItem: any) => {
    if (isPriceLockedView) return;
    if (!myShopItem) return;
    const item = myShopItem?.item;
    if (!item) return;
    setEditMyShopItem((prev) => ({
      ...prev,
      active: true,
      item: item,
      componentId: myShopItem?.id || null,
    }));
  };

  const handleCreateNewMyShopItem = (itemName: any, component: any) => {
    if (isPriceLockedView) return;

    let itemPayload = {
      name: itemName || '',
      categoryId: form?.itemType?.id || null,
      supplierId: form?.supplier?.id || null,
      supplierName: form?.supplier?.displayName || '',
      subcategoryId: component?.category?.id || null,
    };
    setEditMyShopItem((prev) => ({
      ...prev,
      active: true,
      item: null,
      prefill: itemPayload,
      lockPrefill: true,
      componentId: component?.id || null,
    }));
  };

  const handleMyShopModalClose = (item: any) => {
    if (item) {
      setBuildFromMyShopComponents((prev) => {
        return prev.map((i) => {
          let foundSubcategory = subCategories?.find((f) => f.id === item?.subcategoryId);

          let new_form = { ...i, item: item };

          if (foundSubcategory) {
            new_form['category'] = foundSubcategory;
          }

          let changedSupplier = form?.supplier?.id !== item?.supplierId;
          if (changedSupplier && i?.item?.id === item?.id) {
            return { ...initialBuildFromMyShopComponent, id: i?.id };
          }
          // edit
          if (i?.item?.id === item?.id) {
            return new_form;
          }
          // new
          if (editMyShopItem.componentId === i?.id) {
            return new_form;
          }
          return i;
        });
      });
    }
    setRefetchItems((prev) => !prev);
    setEditMyShopItem(initialEditMyShopItem);
  };

  const handleValidateForm = () => {
    let check_case = false;
    let check_client = false;
    let check_room = false;
    let check_item_type = false;

    for (const [key, value] of Object.entries(form)) {
      switch (key) {
        case 'client':
          const client_case = !value;
          setFormErrors((prev) => ({
            ...prev,
            [key]: { active: client_case, message: 'Client is required' },
          }));

          if (client_case) {
            handleScrollToField('client');
          }

          check_client = !client_case;
          break;
        case 'room':
          const room_case = !value;
          setFormErrors((prev) => ({
            ...prev,
            [key]: { active: room_case, message: 'Room is required' },
          }));

          if (room_case) {
            handleScrollToField('room');
          }

          check_room = !room_case;
          break;
        case 'itemType':
          const itemType_case = !value;
          setFormErrors((prev) => ({
            ...prev,
            [key]: { active: itemType_case, message: 'Category is required' },
          }));

          if (itemType_case) {
            handleScrollToField('itemType');
          }

          check_item_type = !itemType_case;
          break;
        default:
          break;
      }
    }

    if (check_client && check_room && check_item_type) {
      check_case = true;
    }

    return check_case;
  };

  const enterTotalPrice = useMemo(() => {
    let customQuantity = Number(buildFromMyShopComponentsPricing?.customQuantity) || 1;
    let customCost = Number(eleminateCurrency(buildFromMyShopComponentsPricing?.customCost)) || 0;
    let customMarkup = Number(buildFromMyShopComponentsPricing?.customMarkup) || 0;
    let temp_additionalPriceAdjustment = isLocked
      ? buildFromMyShopComponentsPricing.customClientPriceAdjustmentType || ''
      : additionalPriceAdjustment || '';
    let temp_additionalPriceAdjustmentValue = isLocked
      ? buildFromMyShopComponentsPricing.customClientPriceAdjustment || ''
      : additionalPriceAdjustmentValue || '';

    const markup_or_discount_parsed = Number(temp_additionalPriceAdjustmentValue) || 0;
    const markup_or_discount =
      temp_additionalPriceAdjustment === 'discount'
        ? -markup_or_discount_parsed
        : markup_or_discount_parsed;

    let total_markup = 1 + customMarkup / 100 + markup_or_discount / 100;

    const preTotal = customQuantity * customCost * total_markup;
    const total = preTotal;

    return total.toFixed(2);
  }, [
    buildFromMyShopComponentsPricing,
    buildFromMyShopComponentsShipping,
    additionalPriceAdjustment,
    additionalPriceAdjustmentValue,
    isLocked,
  ]);

  useEffect(() => {
    if (!shouldAutoRecalculateBuildPricingRef.current) return;
    setBuildFromMyShopComponentsPricing((prev) => {
      if (prev?.customCustomerFacingPrice === enterTotalPrice) return prev;

      return {
        ...prev,
        customCustomerFacingPrice: `$${enterTotalPrice}`,
      };
    });
  }, [enterTotalPrice]);

  const syncSavedCopies = (savedUploads: any[] = imageUploads) => {
    setFormCopy(form);
    setComponentsTotalFormCopy(componentsTotalForm);
    setWhoWillOrderCopy(whoWillOrder);
    setAlternateSalesDescriptionCopy(alternateSalesDescription);
    setBuildFromMyShopComponentsCopy(buildFromMyShopComponents);
    setBuildFromMyShopComponentsShippingCopy(buildFromMyShopComponentsShipping);
    setBuildFromMyShopComponentsPricingCopy(buildFromMyShopComponentsPricing);
    setDocumentStateCopy(documentState);
    setImageUploadsCopy(savedUploads);
  };

  const handleSubmit = async (
    saveMode: SaveMode = primarySaveMode,
    options: SubmitOptions = {}
  ) => {
    const { autoSave = false } = options;
    const validate = handleValidateForm();
    if (!validate) {
      toast.error('Required Fields Missing', {
        description: 'Please fill in all required fields marked with an asterisk (*)',
        duration: 3000,
      });
      setIsChanged(false);
      return false;
    }

    if (!validateActiveBuildComponents()) {
      setIsChanged(false);
      return false;
    }

    handleLoading(true);
    setIsChanged(false);

    const formData = new FormData();
    const files = imageUploads;
    const useDirectUpload = Config.fileStoreUrl.includes('localhost');
    const remoteUploadsPayload: Array<{
      source: string;
      type: string;
      remoteKey?: string;
      description?: string;
    }> = [];

    if (files && files.length > 0) {
      for (const f of files) {
        const hasRemoteUpload =
          (f.remoteUploaded && (f.remoteSource || f.remoteKey)) || (!!f.remoteSource && f.isPdf);

        if (hasRemoteUpload) {
          const remoteSource =
            (typeof f.remoteSource === 'string' && f.remoteSource.length > 0
              ? f.remoteSource
              : typeof f.remoteKey === 'string'
                ? `/${f.remoteKey}`
                : '') || '';

          if (remoteSource) {
            remoteUploadsPayload.push({
              source: remoteSource,
              type: f.fileType || f.mimeType || f.type || 'application/pdf',
              remoteKey: f.remoteKey,
              description: f.description || '',
            });
          }
          continue;
        }

        if (!f.blobToSend) {
          console.error('No blob to send');
          continue;
        }

        if (useDirectUpload) {
          const croppedFile = new File([f.blobToSend], f.name, {
            type: f.type,
            lastModified: Date.now(),
          });
          formData.append(`files`, croppedFile);
          formData.append(`descriptions`, '');
          continue;
        }

        const normalizedFileType = (f.fileType || f.mimeType || f.type || '')
          .toString()
          .toLowerCase();
        const uploadType = normalizedFileType.includes('pdf') ? 'pdf' : 'image';
        const remoteInfo = await uploadFileWithPresign({
          accountId,
          contentType: f.type || f.mimeType || undefined,
          file: f.blobToSend,
          fileName: f.name || `upload.${uploadType === 'pdf' ? 'pdf' : 'png'}`,
          uploadType,
        });
        remoteUploadsPayload.push({
          source: remoteInfo.remoteSource,
          type: remoteInfo.normalizedType,
          remoteKey: remoteInfo.remoteKey,
          description: f.description || '',
        });
      }
    }
    if (remoteUploadsPayload.length > 0) {
      formData.append('remoteUploads', JSON.stringify(remoteUploadsPayload));
    }
    formData.append('accountId', accountId.toString());
    formData.append('fileStoreUrl', Config.fileStoreUrl);
    formData.append('preserveFileName', 'true');

    const documentStateForSave =
      duplicate || !existingId
        ? initialPurchasedGoodsDocumentState
        : // : normalizePurchasedGoodsPoDocumentState(documentState);
          documentState;

    const mappedBuildFromMyShopComponents = (
      buildFromMyShopComponents || [initialBuildFromMyShopComponent]
    )?.map((i) => {
      const tempMarkup = resolveItemBillingTypeClientBased(form?.client, i?.item)?.value;
      const shouldApplyHeaderPo = !duplicate && componentsType === 'components-build';
      return {
        ...i,
        ...(documentStateForSave.poStatus === 'Received'
          ? {
              receivedDate: new Date(),
            }
          : {}),
        ...(shouldApplyHeaderPo
          ? {
              po: i?.poGenerated ? i?.po : documentStateForSave.poNumber || '',
              poStatus: documentStateForSave.poStatus || 'Not Created',
              orderedDate: documentStateForSave.poDate || null,
            }
          : {}),
        cost: isLocked
          ? eleminateCurrency(i?.cost)
          : i?.item
            ? !resolveItemBillingTypeClientBased(form?.client, i?.item)?.method?.includes(
                'markUp'
              ) || !i?.item?.cost
              ? Number(eleminateCurrency(i?.cost))
                ? eleminateCurrency(i?.cost)
                : resolveItemBillingTypeClientBased(form?.client, i?.item)?.value
              : i?.item?.cost
            : eleminateCurrency(i?.cost),
        markup: isLocked
          ? i?.markup
          : !resolveItemBillingTypeClientBased(form?.client, i?.item)?.method?.includes('markUp')
            ? ''
            : tempMarkup,
        clientPriceAdjustment: isLocked
          ? i?.clientPriceAdjustment
          : additionalPriceAdjustmentValue || '',
        clientPriceAdjustmentType: isLocked
          ? i?.clientPriceAdjustmentType
          : additionalPriceAdjustment || '',
        calculatedPrice: isLocked
          ? i?.calculatedPrice
          : calculateComponentPrice(
              {
                ...i,
                cost: isLocked
                  ? eleminateCurrency(i?.cost)
                  : i?.item
                    ? !resolveItemBillingTypeClientBased(form?.client, i?.item)?.method?.includes(
                        'markUp'
                      ) || !i?.item?.cost
                      ? Number(eleminateCurrency(i?.cost)) ||
                        resolveItemBillingTypeClientBased(form?.client, i?.item)?.value
                      : i?.item?.cost || ''
                    : eleminateCurrency(i?.cost),
                markup: !resolveItemBillingTypeClientBased(form?.client, i?.item)?.method?.includes(
                  'markUp'
                )
                  ? ''
                  : isLocked
                    ? i?.markup
                    : resolveItemBillingTypeClientBased(form?.client, i?.item)?.value,
              },
              {
                value: isLocked ? i?.clientPriceAdjustment : additionalPriceAdjustmentValue,
                type: isLocked ? i?.clientPriceAdjustmentType : additionalPriceAdjustment,
              }
            ),
      };
    });

    const buildFromMyShopComponentsShippingPayload = {
      ...buildFromMyShopComponentsShipping,
      price: isLocked
        ? eleminateCurrency(buildFromMyShopComponentsShipping?.price)
        : buildShippingRef?.current?.price,
      cost: isLocked
        ? eleminateCurrency(buildFromMyShopComponentsShipping?.cost)
        : buildShippingRef?.current?.cost,
      clientPriceAdjustment: isLocked
        ? buildFromMyShopComponentsShipping.clientPriceAdjustment
        : additionalPriceAdjustmentValue,
      clientPriceAdjustmentType: isLocked
        ? buildFromMyShopComponentsShipping.clientPriceAdjustmentType
        : additionalPriceAdjustment,
    };
    const buildFromMyShopComponentsPricingPayload = {
      ...buildFromMyShopComponentsPricing,
      type: pricingType,
      cost: buildPricingRef?.current?.sum?.cost || '',
      customCost: eleminateCurrency(buildFromMyShopComponentsPricing?.customCost) || '',
      markup: buildPricingRef?.current?.sum?.markup || '',
      price: buildPricingRef?.current?.sum?.price || '',
      customPrice: eleminateCurrency(enterTotalPrice) || '',
      quantity: '1',
      customQuantity: buildFromMyShopComponentsPricing?.customQuantity || '1',
      customerFacingPrice: eleminateCurrency(buildFromMyShopComponentsPricing.customerFacingPrice),
      customCustomerFacingPrice: eleminateCurrency(
        buildFromMyShopComponentsPricing.customCustomerFacingPrice
      ),
      clientPriceAdjustment: isLocked
        ? buildFromMyShopComponentsPricing.clientPriceAdjustment
        : additionalPriceAdjustmentValue,
      clientPriceAdjustmentType: isLocked
        ? buildFromMyShopComponentsPricing.clientPriceAdjustmentType
        : additionalPriceAdjustment,
      customClientPriceAdjustment: isLocked
        ? buildFromMyShopComponentsPricing?.customClientPriceAdjustment
        : additionalPriceAdjustmentValue,
      customClientPriceAdjustmentType: isLocked
        ? buildFromMyShopComponentsPricing?.customClientPriceAdjustmentType
        : additionalPriceAdjustment,
    };
    const purchasedGoodsTotalRow = {
      // There can be 2 types (totalOnly, fromMyShop)
      type: 'totalOnly',
      estimateDescription: componentsTotalForm?.estimateDescription || '',
      qty: componentsTotalForm?.qty || '',
      quantity: componentsTotalForm?.qty || '',
      po: documentStateForSave.poNumber || '',
      poStatus: documentStateForSave.poStatus || 'Not Created',
      orderedDate: documentStateForSave.poDate || null,
      cost: eleminateCurrency(componentsTotalForm?.cost || ''),
      markup: componentsTotalForm?.markup || '',
      calculatedPrice: eleminateCurrency(componentsTotalForm?.calculatedPrice || ''),
      customerFacingPrice: eleminateCurrency(componentsTotalForm?.customerFacingPrice || ''),
    };
    const purchasedGoodsShippingPayload = {
      checked: shippingAndHandling,
      shippingAndHandlingDescription: componentsTotalForm?.shippingAndHandlingDescription || '',
      shippingAndHandlingQty: componentsTotalForm?.shippingAndHandlingQty || '',
      shippingAndHandlingCost: eleminateCurrency(
        componentsTotalForm?.shippingAndHandlingCost || ''
      ),
      shippingAndHandlingMarkup: componentsTotalForm?.shippingAndHandlingMarkup || '',
      shippingAndHandlingCalculatedPrice: eleminateCurrency(
        componentsTotalForm?.shippingAndHandlingCalculatedPrice || ''
      ),
      shippingAndHandlingCustomerFacingPrice: eleminateCurrency(
        componentsTotalForm?.shippingAndHandlingCustomerFacingPrice || ''
      ),
    };

    const payload = {
      hardwareAdditionalPriceAdjustment: additionalPriceAdjustmentValue,
      hardwareAdditionalPriceAdjustmentType: additionalPriceAdjustment,
      accountId: accountId || null,
      clientId: form?.client?.id || null,
      roomId: form?.room?.id || null,
      purchasedGoodsItemTypeId: form?.itemType?.id || null,
      purchasedGoodsSupplierId: form?.supplier?.id || null,
      purchasedGoodsPurchaser: whoWillOrder === 'workroom' ? 1 : 2,
      purchasedGoodsNotes: form?.notes || null,
      purchasedGoodsWindowLocationId: form?.window_location_id?.trim() || null,
      purchasedGoodsComponents: [purchasedGoodsTotalRow],
      purchasedGoodsShippingAndHandling: purchasedGoodsShippingPayload,
      isLocked: saveMode === 'lock',
      purchasedGoodsComponentsType: componentsType || 'components-build',
      buildFromMyShopComponents: mappedBuildFromMyShopComponents,
      alternateSalesDescription: alternateSalesDescription,
      buildFromMyShopComponentsShipping: buildFromMyShopComponentsShippingPayload || null,
      buildFromMyShopComponentsPricing: buildFromMyShopComponentsPricingPayload || null,
      estimateStatus: documentStateForSave.estimateStatus || 'Not Created',
      estimateNumber: documentStateForSave.estimateNumber || null,
      qbEstimateNumber: documentStateForSave.estimateNumber || null,
      estimateDate: documentStateForSave.estimateDateSent || null,
      estimateDateSent: documentStateForSave.estimateDateSent || null,
      invoiceStatus: documentStateForSave.invoiceStatus || 'Not Created',
      invoicePaid: documentStateForSave.invoiceStatus || 'Not Created',
      invoiceNumber: documentStateForSave.invoiceNumber || null,
      invoiceDate: documentStateForSave.invoiceDateSent || null,
      invoiceDateSent: documentStateForSave.invoiceDateSent || null,
    };

    try {
      let savedUploads: any[] | null = null;

      if (existingId) {
        if (existingUploadsId) {
          formData.append('id', existingUploadsId.toString());
          const uploadEditResponse = await editDraperyOrderUpload(formData);
          payload['draperyOrderUploadId'] = uploadEditResponse?.id || existingUploadsId;
          if (uploadEditResponse?.items) {
            savedUploads = mapStoredUploadsToRemoteItems(uploadEditResponse.items);
          }
        } else if (files?.length > 0 || remoteUploadsPayload.length > 0) {
          const uploadAddResponse = await addDraperyOrderUpload(formData);
          payload['draperyOrderUploadId'] = uploadAddResponse.id;
          setExistingUploadsId(uploadAddResponse.id || 0);
          if (uploadAddResponse?.items) {
            savedUploads = mapStoredUploadsToRemoteItems(uploadAddResponse.items);
          }
        }

        payload['ids'] = [existingId];
        const response = await updatePurchasedGoods(payload);
        if (!response) {
          console.error('Error while updating purchased goods');
          handleLoading(false);
          return false;
        }
      } else {
        const uploadAddResponse = await addDraperyOrderUpload(formData);
        payload['uploadsId'] = uploadAddResponse.id;
        if (uploadAddResponse?.items) {
          savedUploads = mapStoredUploadsToRemoteItems(uploadAddResponse.items);
        }
        let notebookData = notebookRef?.current?.getData() || [];
        payload['notebookItems'] = notebookData?.['items']?.map((n: any) => {
          return {
            tabType: n.type,
            table: n.table,
            creatorId: n?.creator?.id,
            content: n.content || null,
            resolved: false,
            resolvedById: null,
            orderId: null,
            projectId: null,
            mentionedUserIds: n.mentionedUserIds,
          };
        });
        const response = await createPurchasedGoods(payload);
        if (!response) {
          console.error('Error while creating purchased goods');
          handleLoading(false);
          return false;
        }
      }

      if (savedUploads) {
        setImageUploads(savedUploads);
      }

      const currentUploads = savedUploads || imageUploads;

      trackView(saveMode === 'lock' ? MixpanelEvents.SAVE_FINAL : MixpanelEvents.SAVE_DRAFT, {
        item_type: 'purchased-goods',
        item_status: resolvePurchasedGoodsSavedStatus(saveMode),
        estimate_status: documentStateForSave.estimateStatus || 'Not Created',
        uploads: resolveUploadsStatus(currentUploads),
      });

      if (autoSave) {
        syncSavedCopies(currentUploads);
        setIsLocked(saveMode === 'lock');
        setPersistedPriceLocked(saveMode === 'lock');
        toast('Saved', {
          description: 'Changes saved',
        });
        handleLoading(false);
        if (onAutoSave) onAutoSave();
        return true;
      }

      toast('Success!', {
        description: duplicate
          ? 'Item duplicated successfully'
          : saveMode === 'lock'
            ? 'Purchased goods item saved and price locked'
            : 'Purchased goods item saved as draft',
      });
      handleLoading(false);
      handleResetForm();
      handleSetDuplicate(false);
      if (onSave) onSave();
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Unable to save purchased goods item');
      handleLoading(false);
      return false;
    }
  };

  const handleShowDeleteModal = (val: boolean) => {
    setShowDeleteModal(val);
  };

  const handleDeleteModal = async () => {
    if (!existingId) return;
    handleShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    handleShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!existingId) return;
    const payload = {
      ids: [existingId],
    };
    const response = await deletePurchasedGoods(payload);
    if (response) {
      handleResetForm();
      toast('Item deleted', {
        description: 'The item has been successfully deleted',
      });
      handleShowDeleteModal(false);
      if (onDelete) onDelete();
    }
  };

  const handleFetchExistingGood = async (id: any) => {
    setDisabled(true);
    const response = await getSinglePurchasedGood(id);
    const nextDocumentState = buildPurchasedGoodsDocumentState(response, duplicate);
    setDocumentState(nextDocumentState);
    setDocumentStateCopy(nextDocumentState);

    setBuildFromMyShopComponentsShipping(
      response?.orderComponentsShipping
        ? mapBuildShippingStateForForm(response?.orderComponentsShipping)
        : initialBuildShipping
    );
    setBuildFromMyShopComponentsShippingCopy(
      response?.orderComponentsShipping
        ? mapBuildShippingStateForForm(response?.orderComponentsShipping)
        : initialBuildShipping
    );
    setBuildFromMyShopComponentsPricing(
      response?.orderComponentsPricing
        ? {
            ...response?.orderComponentsPricing,
            cost: response?.orderComponentsPricing?.cost
              ? `$${response?.orderComponentsPricing?.cost}`
              : '',
            price: response?.orderComponentsPricing?.price
              ? `$${response?.orderComponentsPricing?.price}`
              : '',
            customerFacingPrice: response?.orderComponentsPricing?.customerFacingPrice
              ? `$${response?.orderComponentsPricing?.customerFacingPrice}`
              : '',
            customCost: response?.orderComponentsPricing?.customCost
              ? `$${response?.orderComponentsPricing?.customCost}`
              : '',
            customPrice: response?.orderComponentsPricing?.customPrice
              ? `$${response?.orderComponentsPricing?.customPrice}`
              : '',
            customCustomerFacingPrice: response?.orderComponentsPricing?.customCustomerFacingPrice
              ? `$${response?.orderComponentsPricing?.customCustomerFacingPrice}`
              : '',
          }
        : initialBuildPricing
    );
    setBuildFromMyShopComponentsPricingCopy(
      response?.orderComponentsPricing
        ? {
            ...response?.orderComponentsPricing,
            cost: response?.orderComponentsPricing?.cost
              ? `$${response?.orderComponentsPricing?.cost}`
              : '',
            price: response?.orderComponentsPricing?.price
              ? `$${response?.orderComponentsPricing?.price}`
              : '',
            customerFacingPrice: response?.orderComponentsPricing?.customerFacingPrice
              ? `$${response?.orderComponentsPricing?.customerFacingPrice}`
              : '',
            customCost: response?.orderComponentsPricing?.customCost
              ? `$${response?.orderComponentsPricing?.customCost}`
              : '',
            customPrice: response?.orderComponentsPricing?.customPrice
              ? `$${response?.orderComponentsPricing?.customPrice}`
              : '',
            customCustomerFacingPrice: response?.orderComponentsPricing?.customCustomerFacingPrice
              ? `$${response?.orderComponentsPricing?.customCustomerFacingPrice}`
              : '',
          }
        : initialBuildPricing
    );

    let hasCFPsaved =
      response?.orderComponentsPricing?.type === 'automatic'
        ? response?.orderComponentsPricing?.customerFacingPrice
          ? false
          : true
        : response?.orderComponentsPricing?.type === 'custom'
          ? response?.orderComponentsPricing?.customCustomerFacingPrice
            ? false
            : true
          : true;
    shouldAutoRecalculateBuildPricingRef.current = hasCFPsaved;

    setComponentsType(response?.purchasedGoodsComponentsType || 'components-total');
    setPricingType(response?.orderComponentsPricing?.type || 'automatic');
    setAlternateSalesDescription(response?.alternateSalesDescription || '');
    setAlternateSalesDescriptionCopy(response?.alternateSalesDescription || '');

    setIsLocked(response?.isLocked || false);
    setPersistedPriceLocked(response?.isLocked || false);
    setUnlockedForEditing(false);
    setUnlockDialogOpen(false);
    setComponentRowErrors({});
    setExistingUploadsId(response?.draperyOrderUpload?.id || 0);

    if (response?.draperyOrderUpload) {
      const remoteUploads = mapStoredUploadsToRemoteItems(response?.draperyOrderUpload?.items);
      setImageUploads(remoteUploads);
      setImageUploadsCopy(remoteUploads);
    }

    const totalOnlyComponents: any[] = response?.purchasedGoodsComponents?.filter(
      (f: any) => f.type === 'totalOnly'
    );
    const totalOnly = totalOnlyComponents?.[0] || {};
    const shippingAndHandling: any = response?.purchasedGoodsShippingAndHandling;
    setClientBillingInfo({
      additionalPriceAdjustment:
        response?.draperyOrderHardware?.hardwareAdditionalPriceAdjustmentType,
      additionalPriceAdjustmentValue:
        response?.draperyOrderHardware?.hardwareAdditionalPriceAdjustment,
    });

    setForm({
      client: response?.client || null,
      room: response?.room || null,
      itemType: response?.purchasedGoodsItemType || null,
      supplier: response?.purchasedGoodsSupplier || null,
      notes: response?.purchasedGoodsNotes || '',
      window_location_id: response?.purchasedGoodsWindowLocationId || '',
    });
    setFormCopy({
      client: response?.client || null,
      room: response?.room || null,
      itemType: response?.purchasedGoodsItemType || null,
      supplier: response?.purchasedGoodsSupplier || null,
      notes: response?.purchasedGoodsNotes || '',
      window_location_id: response?.purchasedGoodsWindowLocationId || '',
    });

    setComponentsTotalForm({
      estimateDescription: totalOnly?.estimateDescription,
      qty: totalOnly?.qty,
      cost: `$${totalOnly?.cost}`,
      markup: totalOnly?.markup,
      calculatedPrice: totalOnly?.calculatedPrice,
      customerFacingPrice: `$${totalOnly?.customerFacingPrice}`,
      shippingAndHandlingDescription: shippingAndHandling.shippingAndHandlingDescription,
      shippingAndHandlingQty: shippingAndHandling.shippingAndHandlingQty,
      shippingAndHandlingCost: `$${shippingAndHandling.shippingAndHandlingCost}`,
      shippingAndHandlingMarkup: shippingAndHandling.shippingAndHandlingMarkup,
      shippingAndHandlingCalculatedPrice: shippingAndHandling.shippingAndHandlingCalculatedPrice,
      shippingAndHandlingCustomerFacingPrice: `$${shippingAndHandling.shippingAndHandlingCustomerFacingPrice}`,
    });
    setComponentsTotalFormCopy({
      estimateDescription: totalOnly?.estimateDescription,
      qty: totalOnly?.qty,
      cost: `$${totalOnly?.cost}`,
      markup: totalOnly?.markup,
      calculatedPrice: totalOnly?.calculatedPrice,
      customerFacingPrice: `$${totalOnly?.customerFacingPrice}`,
      shippingAndHandlingDescription: shippingAndHandling.shippingAndHandlingDescription,
      shippingAndHandlingQty: shippingAndHandling.shippingAndHandlingQty,
      shippingAndHandlingCost: `$${shippingAndHandling.shippingAndHandlingCost}`,
      shippingAndHandlingMarkup: shippingAndHandling.shippingAndHandlingMarkup,
      shippingAndHandlingCalculatedPrice: shippingAndHandling.shippingAndHandlingCalculatedPrice,
      shippingAndHandlingCustomerFacingPrice: `$${shippingAndHandling.shippingAndHandlingCustomerFacingPrice}`,
    });

    let res_build_my_shop =
      response?.purchasedGoodsComponentsBuild && response?.purchasedGoodsComponentsBuild?.length > 0
        ? response?.purchasedGoodsComponentsBuild?.map((i: any) => ({
            ...i,
            cost: i?.cost ? `$${i?.cost}` : '',
          }))
        : initialBuildFromMyShopComponents;

    setBuildFromMyShopComponents(res_build_my_shop);
    setBuildFromMyShopComponentsCopy(res_build_my_shop);

    setWhoWillOrder(
      response?.purchasedGoodsPurchaser
        ? response?.purchasedGoodsPurchaser === 1
          ? 'workroom'
          : 'com'
        : 'workroom'
    );
    setWhoWillOrderCopy(
      response?.purchasedGoodsPurchaser
        ? response?.purchasedGoodsPurchaser === 1
          ? 'workroom'
          : 'com'
        : 'workroom'
    );
    setShippingAndHandling(shippingAndHandling?.checked || false);
    setDisabled(response?.archived);
  };

  const handleDuplicateGoods = () => {
    handleLoading(true);

    setTimeout(() => {
      handleSetDuplicate(true);
      setIsLocked(false);
      setPersistedPriceLocked(false);
      setUnlockedForEditing(false);
      setComponentRowErrors({});
      refreshComponentsFromLiveItems();
      setForm((prev) => ({ ...prev, client: null, room: null, notes: '', window_location_id: '' }));
      setDocumentState(initialPurchasedGoodsDocumentState);
      setDocumentStateCopy(initialPurchasedGoodsDocumentState);
      setClientBillingInfo({
        additionalPriceAdjustment: null,
        additionalPriceAdjustmentValue: null,
      });
      // setFormCopy((prev) => ({ ...prev, client: null, room: null }));
      toast('Item Duplicated', {
        description: 'Please select a new client and room for this copy.',
      });
      if (onDuplicate) onDuplicate();
      handleLoading(false);

      const client_input_el = document.getElementById('client');
      const room_input_el = document.getElementById('room');

      if (client_input_el) {
        client_input_el.classList.add('blink-border');
        setTimeout(() => {
          client_input_el.classList.remove('blink-border');
        }, 1550);
      }
      if (room_input_el) {
        room_input_el.classList.add('blink-border');
        setTimeout(() => {
          room_input_el.classList.remove('blink-border');
        }, 1550);
      }
    }, 1000);
  };

  const handleConfirmUnlockPrice = () => {
    setUnlockedForEditing(true);
    setIsLocked(false);
    setUnlockDialogOpen(false);
    setComponentRowErrors({});
    refreshComponentsFromLiveItems(true);
  };

  // Use effect for prefilling when duplicate is done through 3dot menu
  useEffect(() => {
    if (purchasedGoodsPrefill) {
      if (purchasedGoodsPrefill?.item) {
        const executePrefill = async () => {
          let prefillItem = purchasedGoodsPrefill.item;
          let prefillWhoWillOrder = prefillItem?.purchasedGoodsPurchaser === 1 ? 'workroom' : 'com';
          const totalOnlyComponents: any[] = prefillItem?.purchasedGoodsComponents?.filter(
            (f: any) => f.type === 'totalOnly'
          );
          const totalOnly = totalOnlyComponents?.[0] || {};
          const shippingAndHandling: any = prefillItem?.purchasedGoodsShippingAndHandling;

          setDuplicate(purchasedGoodsPrefill?.duplicate || false);
          setIsLocked(false);
          setPersistedPriceLocked(false);
          setUnlockedForEditing(false);
          setComponentRowErrors({});
          setDocumentState(initialPurchasedGoodsDocumentState);
          setDocumentStateCopy(initialPurchasedGoodsDocumentState);
          setShippingAndHandling(prefillItem?.purchasedGoodsShippingAndHandling?.checked || false);
          setWhoWillOrder(prefillWhoWillOrder);
          setForm((prev) => {
            return {
              ...prev,
              client: null,
              room: null,
              itemType: prefillItem?.purchasedGoodsItemType || null,
              supplier: prefillItem?.purchasedGoodsSupplier || null,
              notes: '',
              window_location_id: '',
            };
          });
          setComponentsTotalForm({
            estimateDescription: totalOnly?.estimateDescription,
            qty: totalOnly?.qty,
            cost: `$${totalOnly?.cost}`,
            markup: totalOnly?.markup,
            calculatedPrice: totalOnly?.calculatedPrice,
            customerFacingPrice: `$${totalOnly?.customerFacingPrice}`,
            shippingAndHandlingDescription: shippingAndHandling.shippingAndHandlingDescription,
            shippingAndHandlingQty: shippingAndHandling.shippingAndHandlingQty,
            shippingAndHandlingCost: `$${shippingAndHandling.shippingAndHandlingCost}`,
            shippingAndHandlingMarkup: shippingAndHandling.shippingAndHandlingMarkup,
            shippingAndHandlingCalculatedPrice:
              shippingAndHandling.shippingAndHandlingCalculatedPrice,
            shippingAndHandlingCustomerFacingPrice: `$${shippingAndHandling.shippingAndHandlingCustomerFacingPrice}`,
          });
          setExistingUploadsId(prefillItem?.draperyOrderUpload?.id || 0);
          if (prefillItem?.draperyOrderUpload) {
            setImageUploads(mapStoredUploadsToRemoteItems(prefillItem?.draperyOrderUpload?.items));
          }
          if (prefillItem?.purchasedGoodsComponentsBuild) {
            setBuildFromMyShopComponents(
              prefillItem.purchasedGoodsComponentsBuild.map(replaceComponentSnapshotWithLiveItem)
            );
          }
          if (prefillItem?.orderComponentsShipping) {
            setBuildFromMyShopComponentsShipping(
              mapBuildShippingStateForForm(prefillItem.orderComponentsShipping)
            );
          }
          if (prefillItem?.orderComponentsPricing) {
            setBuildFromMyShopComponentsPricing(prefillItem.orderComponentsPricing);
            setPricingType(prefillItem.orderComponentsPricing?.type || 'automatic');
          }

          setTimeout(() => {
            toast('Item Duplicated', {
              description: 'Please select a new client and room for this copy.',
            });

            const client_input_el = document.getElementById('client');
            const room_input_el = document.getElementById('room');

            if (client_input_el) {
              client_input_el.classList.add('blink-border');
              setTimeout(() => {
                client_input_el.classList.remove('blink-border');
              }, 1550);
            }
            if (room_input_el) {
              room_input_el.classList.add('blink-border');
              setTimeout(() => {
                room_input_el.classList.remove('blink-border');
              }, 1550);
            }
          }, 1000);
        };

        executePrefill();
      }
    }
  }, [purchasedGoodsPrefill]);

  useEffect(() => {
    if (isLocked) return;

    const billingDetails = form?.client?.billToParent
      ? form?.client?.billingDetails || form?.client?.billingInfo
      : form?.client?.billingDetailsDirectly || form?.client?.billingInfoDirectly;
    setClientBillingInfo((prev: any) => {
      return {
        ...prev,
        additionalPriceAdjustment: billingDetails?.additionalPriceAdjustment || null,
        additionalPriceAdjustmentValue: billingDetails?.additionalPriceAdjustmentValue || null,
      };
    });
  }, [form.client, isLocked]);

  useEffect(() => {
    if (form.client && !isLocked) {
      const calculateComponentsTemp = (newForm: any) => {
        // Additional client price adjustment
        const markup_or_discount_parsed = Number(additionalPriceAdjustmentValue) || 0;
        const markup_or_discount =
          additionalPriceAdjustment === 'discount'
            ? -markup_or_discount_parsed
            : markup_or_discount_parsed;

        // For components
        const parsedQty = Number(eleminateCurrency(newForm.qty) || 0);
        const parsedCost = Number(eleminateCurrency(newForm.cost) || 0);
        const parsedMarkup = Number(eleminateCurrency(newForm.markup) || 0);
        const totalPercentage = 1 + parsedMarkup / 100 + markup_or_discount / 100;
        const parsedTotalCustomerFacingPrice = parsedQty * (parsedCost * totalPercentage);

        // For shipping and handling
        const parsedShippmentQty = 1;
        const parsedShippingAndHandlingCost = Number(
          eleminateCurrency(newForm.shippingAndHandlingCost) || 0
        );
        const parsedShippingAndHandlingMarkup = Number(
          eleminateCurrency(newForm.shippingAndHandlingMarkup) || 0
        );
        const totalPercentageShippingAndHandling =
          1 + parsedShippingAndHandlingMarkup / 100 + markup_or_discount / 100;
        const parsedShippingAndHandlingCustomerFacingPrice =
          parsedShippmentQty * (parsedShippingAndHandlingCost * totalPercentageShippingAndHandling);

        return {
          ...newForm,
          // customerFacingPrice: `$${parsedTotalCustomerFacingPrice.toFixed(2)}`,
          calculatedPrice: parsedTotalCustomerFacingPrice.toFixed(2),
          shippingAndHandlingCalculatedPrice:
            parsedShippingAndHandlingCustomerFacingPrice.toFixed(2),
        };
      };
      setComponentsTotalForm((prev) => {
        let newForm = { ...prev };
        return calculateComponentsTemp(newForm);
      });
      setComponentsTotalFormCopy((prev) => {
        let newForm = { ...prev };
        return calculateComponentsTemp(newForm);
      });
    }
  }, [additionalPriceAdjustmentValue, additionalPriceAdjustment, isLocked]);

  useEffect(() => {
    if (prefilledClient && !existingId) {
      setForm((prev) => ({ ...prev, client: prefilledClient }));
      setFormCopy((prev) => ({ ...prev, client: prefilledClient }));
    }
  }, [prefilledClient, existingId]);

  useEffect(() => {
    if (existingId) {
      lockedAutoSaveSignatureRef.current = '';
      handleFetchExistingGood(existingId);
    }
  }, [existingId]);

  useEffect(() => {
    if (open && !existingId) {
      setDocumentState(initialPurchasedGoodsDocumentState);
      setDocumentStateCopy(initialPurchasedGoodsDocumentState);
    }
  }, [open, existingId]);

  useEffect(() => {
    if (!open || !existingId || duplicate) return;

    const refreshHeaderDocuments = async () => {
      try {
        const response = await getSinglePurchasedGood(existingId);
        const nextDocumentState = buildPurchasedGoodsDocumentState(response);
        setDocumentState(nextDocumentState);
        setDocumentStateCopy(nextDocumentState);
      } catch (error) {
        console.error('Failed to refresh purchased goods document state', error);
      }
    };

    window.addEventListener('focus', refreshHeaderDocuments);
    return () => window.removeEventListener('focus', refreshHeaderDocuments);
  }, [open, existingId, duplicate]);

  const lockedNonPricingState = useMemo(
    () => ({
      notes: form?.notes || '',
      documentState,
      uploads: buildUploadSaveSignature(imageUploads),
    }),
    [form?.notes, documentState, imageUploads]
  );

  const lockedNonPricingCopyState = useMemo(
    () => ({
      notes: formCopy?.notes || '',
      documentState: documentStateCopy,
      uploads: buildUploadSaveSignature(imageUploadsCopy),
    }),
    [formCopy?.notes, documentStateCopy, imageUploadsCopy]
  );

  const hasLockedNonPricingChanges =
    isPriceLockedView && !deepEqual(lockedNonPricingState, lockedNonPricingCopyState);

  useEffect(() => {
    if (lockedAutoSaveTimeoutRef.current) {
      clearTimeout(lockedAutoSaveTimeoutRef.current);
      lockedAutoSaveTimeoutRef.current = null;
    }

    if (!open || !existingId || duplicate || loading || disabled || !hasLockedNonPricingChanges) {
      return;
    }

    const signature = JSON.stringify(lockedNonPricingState);
    if (signature === lockedAutoSaveSignatureRef.current) {
      return;
    }

    lockedAutoSaveTimeoutRef.current = setTimeout(async () => {
      if (lockedAutoSaveInFlightRef.current) {
        return;
      }

      lockedAutoSaveInFlightRef.current = true;
      try {
        const saved = await handleSubmit('lock', { autoSave: true });
        if (saved) {
          lockedAutoSaveSignatureRef.current = signature;
        }
      } catch (error) {
        console.error('Failed to auto-save locked purchased goods item', error);
      } finally {
        lockedAutoSaveInFlightRef.current = false;
      }
    }, 900);

    return () => {
      if (lockedAutoSaveTimeoutRef.current) {
        clearTimeout(lockedAutoSaveTimeoutRef.current);
        lockedAutoSaveTimeoutRef.current = null;
      }
    };
  }, [
    open,
    existingId,
    duplicate,
    loading,
    disabled,
    hasLockedNonPricingChanges,
    lockedNonPricingState,
  ]);

  useEffect(() => {
    if (!open) return;
    loadMyShopItems();
    getCategories(true);
  }, [open, refetchItems]);

  useEffect(() => {
    if (!open || (!duplicate && !unlockedForEditing) || !myShopItems?.length) return;
    refreshComponentsFromLiveItems();
  }, [open, duplicate, unlockedForEditing, myShopItems]);

  useEffect(() => {
    loadClients();
  }, [refetchClient]);

  useEffect(() => {
    if (!open) return;
    loadClients();
    loadItemType();
  }, [open]);

  useEffect(() => {
    loadRooms();
  }, [form.client, refetchRoom]);

  useEffect(() => {
    loadSuppliers();
  }, [refetchSuppliers]);

  useEffect(() => {
    const prevMap = pdfObjectUrlsRef.current;
    const nextMap: Record<string, string> = {};

    if (imageUploads && imageUploads.length > 0) {
      imageUploads.forEach((item: any) => {
        if (!isPdfUpload(item) || item.fromRest) {
          return;
        }
        const source = item.source;
        if (typeof source !== 'string') {
          return;
        }
        if (!source.startsWith('data:')) {
          return;
        }
        if (prevMap[source]) {
          nextMap[source] = prevMap[source];
          return;
        }
        const objectUrl = createPdfObjectUrlFromDataUri(source);
        if (objectUrl) {
          nextMap[source] = objectUrl;
        }
      });
    }

    Object.keys(prevMap).forEach((key) => {
      if (!nextMap[key]) {
        URL.revokeObjectURL(prevMap[key]);
      }
    });

    pdfObjectUrlsRef.current = nextMap;

    let changed = Object.keys(prevMap).length !== Object.keys(nextMap).length;
    if (!changed) {
      for (const key of Object.keys(nextMap)) {
        if (prevMap[key] !== nextMap[key]) {
          changed = true;
          break;
        }
      }
    }

    if (changed) {
      setPdfObjectUrls(nextMap);
    }
  }, [imageUploads]);

  useEffect(() => {
    if (open) {
      // if (buildFromMyShopComponents && buildFromMyShopComponents.length > 0) {
      setTimeout(() => {
        const alt_el = document.getElementById('alternateSalesDescription');
        if (alt_el) {
          alt_el.style.height = `${alt_el.scrollHeight}px`;
        }

        const textareasEl = document.querySelectorAll("textarea[id^='textareaComponents_']");
        if (textareasEl && textareasEl.length > 0) {
          textareasEl.forEach((ta: any) => {
            ta.style.height = '36px';
            if (!ta.value) {
              ta.style.height = '36px';
            } else {
              if (ta.scrollHeight > 36) {
                ta.style.height = '72px';
              } else {
                ta.style.height = '36px';
              }
            }

            const index = ta.id.replace('textareaComponents_', '');
            const dotsEl = document.getElementById(`ta-dots-${index}`);
            if (dotsEl) {
              const hasValue = Boolean(ta.value?.trim());
              const shouldShowDots = hasValue && ta.scrollHeight > 56;
              dotsEl.style.display = shouldShowDots ? 'block' : 'none';
            }
          });
        }
      }, 0);
    }
    // }, [buildFromMyShopComponents]);
  }, [open, disabled]);

  const updateTextareaDotsVisibility = (el: HTMLTextAreaElement, index: number) => {
    const dotsEl = document.getElementById(`ta-dots-${index}`);
    if (!dotsEl) return;

    const hasValue = Boolean(el.value?.trim());
    const shouldShowDots = hasValue && el.scrollHeight > 56;

    dotsEl.style.display = shouldShowDots ? 'block' : 'none';
  };

  const handleOnFocusTextarea = (e: ChangeEvent<HTMLTextAreaElement>, index: number) => {
    const el = e.currentTarget;

    const dots_el = document.getElementById(`ta-dots-${index}`);
    if (dots_el) dots_el.style.display = 'none';

    if (!el) return;
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleOnBlurTextarea = (
    e: ChangeEvent<HTMLTextAreaElement>,
    height: any = 40,
    maxHeight = 72,
    index: number
  ) => {
    const el = e.currentTarget;

    if (el) {
      if (!el.value) {
        el.style.height = `${height}px`;
      } else {
        if (el.scrollHeight <= height) {
          el.style.height = `${height}px`;
        } else if (el.scrollHeight > 56) {
          el.style.height = `${maxHeight}px`;
        }
      }

      updateTextareaDotsVisibility(el, index);
    }
  };

  const calculateTotalPercentage = () => {
    const total_cost =
      Number(componentsTotalForm.qty || 0) *
        Number(eleminateCurrency(componentsTotalForm.cost) || 0) +
      Number(componentsTotalForm.shippingAndHandlingQty || (shippingAndHandling ? 1 : 0)) *
        Number(eleminateCurrency(componentsTotalForm.shippingAndHandlingCost) || 0);
    const total_price =
      Number(eleminateCurrency(componentsTotalForm.calculatedPrice) || 0) +
      Number(eleminateCurrency(componentsTotalForm.shippingAndHandlingCalculatedPrice) || 0);

    if (total_cost <= 0) return '0.00';

    const total = (total_price / total_cost - 1) * 100;

    return total.toFixed(2);
  };

  const getStatusBadgeClassName = () => {
    switch (statusBadgeLabel) {
      case 'Copy Item':
        return 'bg-[#fef3c7] text-[#92400e]';
      case 'Draft':
        return 'bg-pink/10 text-pink';
      case 'Price Locked':
        return 'bg-primary/10 text-primary border border-primary/20';
      case 'New Item':
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const statusBadgeContent = (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeClassName()}`}
    >
      {isPriceLockedView && <LockKeyhole className="h-3 w-3" />}
      {statusBadgeLabel}
    </span>
  );

  const statusBadge = isPriceLockedView ? (
    <Tooltip>
      <TooltipTrigger asChild>{statusBadgeContent}</TooltipTrigger>
      <TooltipContent className="z-[99999991]">
        <span>Price is locked. Unlock to edit pricing.</span>
      </TooltipContent>
    </Tooltip>
  ) : (
    statusBadgeContent
  );

  const saveAndLockTooltipText =
    "Locks this item's price so it won't change if component costs or prices are updated later.";

  const renderSaveModeContent = (mode: SaveMode) => (
    <>
      {mode === 'lock' && <LockKeyhole className="h-4 w-4" />}
      {mode === 'lock' ? 'Save and Lock Price' : 'Save as Draft'}
    </>
  );

  const saveModeButton = (
    <Button
      disabled={nonPricingDisabled}
      variant={'default'}
      onClick={() => handleSubmit(primarySaveMode)}
      type="button"
      className="rounded-r-none"
    >
      {renderSaveModeContent(primarySaveMode)}
    </Button>
  );

  const footerActions = isPriceLockedView ? (
    <Button
      aria-label="Unlock price"
      disabled={nonPricingDisabled}
      variant={'default'}
      onClick={() => setUnlockDialogOpen(true)}
      type="button"
    >
      <LockKeyhole className="h-4 w-4" />
      Unlock Price
    </Button>
  ) : (
    <div className="flex items-center">
      {primarySaveMode === 'lock' ? (
        <Tooltip>
          <TooltipTrigger asChild>{saveModeButton}</TooltipTrigger>
          <TooltipContent
            side="left"
            align="center"
            sideOffset={8}
            className="z-[99999992] max-w-[320px]"
          >
            <span>{saveAndLockTooltipText}</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        saveModeButton
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="More save options"
            disabled={nonPricingDisabled}
            variant={'default'}
            type="button"
            className="rounded-l-none border-l border-white/30 px-2"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[99999991] min-w-[220px]">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleSubmit(alternateSaveMode);
            }}
          >
            {alternateSaveMode === 'lock' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex w-full items-center gap-2">
                    {renderSaveModeContent(alternateSaveMode)}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  align="center"
                  sideOffset={8}
                  className="z-[99999992] max-w-[320px]"
                >
                  <span>{saveAndLockTooltipText}</span>
                </TooltipContent>
              </Tooltip>
            ) : (
              renderSaveModeContent(alternateSaveMode)
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const bottom_sheet_props = {
    open: open,
    onCancel: handleCancel,
    onSave: handleSubmit,
    title: 'Purchased Goods',
    client: form?.client,
    room: form?.room,
    supplier: form?.supplier,
    subTitle: duplicate ? 'Copy Item' : existingId ? 'Edit Item' : 'New Item',
    saveLabel: 'Save as Draft',
    cancelLabel: 'Cancel',
    deleteLabel: 'Delete',
    deleteGoods: !!existingId,
    onDelete: handleDeleteModal,
    disabled: loading || disabled || !hasPurchasedGoodsEditPermission,
    headerActions: true,
    loading: loading,
    existingId: existingId,
    handleDuplicateGoods: handleDuplicateGoods,
    hideDuplicate: duplicate,
    hideSubTitle: true,
    statusBadge: statusBadge,
    headerLocked: isPriceLockedView,
    footerActions: footerActions,
    hideCancel: isPriceLockedView,
    accountUser: accountUser,
    row: existingId
      ? {
          ...form,
          archived: disabled,
          id: existingId,
          notebook: [],
          orderType: 'purchased-goods',
        }
      : null,
    notebookRef: notebookRef,
    orderItemType: form?.itemType?.name,
    roomName: form?.room?.name,
    newOrder: !existingId,
    showIconTitle: true,
    canAccessTeamSettings: canAccessTeamSettings,
    window_location_id: form.window_location_id || '',
  };

  const wide_sheet_quick_add_client_props = {
    active: wideSheetActive,
    onCancel: () => handleShowWideSheet(false),
    getClient: handleSetClient,
    client: undefined,
    clients: flattenedClients,
    accountId: accountId,
    handleRefetchClient: handleRefetchClient,
  };

  const actionModalProps = {
    active: showDeleteModal,
    onCancel: handleCancelDelete,
  };

  const confirmModalProps = {
    style: {
      zIndex: '99999991',
    },
    overlayStyle: {
      zIndex: '99999990',
    },
    refs: confirmModalRef,
    active: isChanged,
    title: 'Confirmation',
    btn_actions: {
      confirm_btn: {
        disabled: disabled,
        action: () => {
          handleSubmit();
        },
        title: 'Yes, save progress',
      },
      cancel_btn: {
        disabled: disabled,
        action: () => {
          handleResetForm();
          setIsChanged(false);
          handleSetDuplicate(false);
          if (onCancel) onCancel();
        },
        title: 'No, cancel edits',
      },
    },
  };

  const buildFromMyShopColsTotal = useMemo(() => {
    let cols =
      9 +
      (additionalPriceAdjustment && additionalPriceAdjustment !== 'none' ? 1 : 0) +
      (isHardwareChosen ? 1 : 0);

    return cols;
  }, [form?.client, isHardwareChosen, additionalPriceAdjustment]);

  const shippingPrice = useMemo(() => {
    const shippingActive = buildFromMyShopComponentsShipping?.active || false;
    const shippingCost = Number(eleminateCurrency(buildFromMyShopComponentsShipping?.cost)) || 0;
    const shippingMarkup = Number(buildFromMyShopComponentsShipping?.markup) || 0;
    const explicitPriceValue = eleminateCurrency(
      buildFromMyShopComponentsShipping?.customerFacingPrice ||
        buildFromMyShopComponentsShipping?.price ||
        ''
    );
    const hasExplicitPrice = explicitPriceValue !== '';
    const explicitPrice = Number(explicitPriceValue) || 0;

    const shippingClientPriceAdjustment = isLocked
      ? Number(buildFromMyShopComponentsShipping?.clientPriceAdjustment) || 0
      : Number(additionalPriceAdjustmentValue) || 0;

    const shippingClientPriceAdjustmentType = isLocked
      ? buildFromMyShopComponentsShipping?.clientPriceAdjustmentType
      : additionalPriceAdjustment;

    const markup_or_discount =
      shippingClientPriceAdjustmentType === 'discount'
        ? -shippingClientPriceAdjustment
        : shippingClientPriceAdjustment;

    let total_markup = 1 + shippingMarkup / 100 + markup_or_discount / 100;
    let computedPrice = shippingCost * total_markup;
    let price = hasExplicitPrice ? explicitPrice : computedPrice;

    buildShippingRef.current = {
      cost: shippingCost,
      markup: shippingMarkup,
      price: price,
    };

    return {
      cost: shippingActive ? shippingCost : 0,
      markup: shippingActive ? shippingMarkup : 0,
      price: shippingActive ? price : 0,
    };
  }, [
    buildFromMyShopComponentsShipping,
    additionalPriceAdjustment,
    additionalPriceAdjustmentValue,
    isLocked,
  ]);

  const pricing = useMemo(() => {
    const result = buildFromMyShopComponents.reduce(
      (acc, val) => {
        const cost = isLocked
          ? Number(eleminateCurrency(val?.cost)) || 0
          : val?.item
            ? !resolveItemBillingTypeClientBased(form?.client, val?.item)?.method?.includes(
                'markUp'
              ) || !val?.item?.cost
              ? Number(eleminateCurrency(val?.cost)) ||
                Number(resolveItemBillingTypeClientBased(form?.client, val?.item)?.value) ||
                0
              : Number(val?.item?.cost) || 0
            : Number(eleminateCurrency(val?.cost)) || 0;
        const qty = Number(val?.quantity) || 1;
        const markup = isLocked
          ? Number(val?.markup) || 0
          : !resolveItemBillingTypeClientBased(form?.client, val?.item)?.method?.includes('markUp')
            ? 0
            : Number(resolveItemBillingTypeClientBased(form?.client, val?.item)?.value) || 0;

        let temp_additionalPriceAdjustment = isLocked
          ? val?.clientPriceAdjustmentType || ''
          : additionalPriceAdjustment || '';
        let temp_additionalPriceAdjustmentValue = isLocked
          ? val?.clientPriceAdjustment || ''
          : additionalPriceAdjustmentValue || '';

        const markup_or_discount_parsed = Number(temp_additionalPriceAdjustmentValue) || 0;
        const markup_or_discount =
          temp_additionalPriceAdjustment === 'discount'
            ? -markup_or_discount_parsed
            : markup_or_discount_parsed;

        const total_percentage = 1 + markup / 100 + markup_or_discount / 100;

        acc.sum.cost += cost * qty;
        acc.sum.price += cost * total_percentage * qty;
        acc.sum.rawTotal += cost * (1 + markup / 100) * qty;

        return acc;
      },
      {
        sum: {
          cost: 0,
          markup: 0,
          price: 0,
          rawTotal: 0,
        },
      }
    );

    result.sum.markup =
      result.sum.cost > 0
        ? Number((((result.sum.rawTotal - result.sum.cost) / result.sum.cost) * 100).toFixed(2))
        : 0;

    buildPricingRef.current = result;

    return result;
  }, [
    buildFromMyShopComponents,
    pricingType,
    form?.client,
    additionalPriceAdjustment,
    additionalPriceAdjustmentValue,
    isLocked,
  ]);

  const hasCustomerFacingDescription = useMemo(
    () => Boolean(String(alternateSalesDescription || '').trim()),
    [alternateSalesDescription]
  );

  const buildMainCustomerFacingPriceInputDisabled =
    pricingFieldsDisabled || !hasCustomerFacingDescription;
  const buildShippingCustomerFacingPriceInputDisabled = pricingFieldsDisabled;

  const buildTotals = useMemo(() => {
    const customQuantity = Number(buildFromMyShopComponentsPricing?.customQuantity) || 1;
    const customCost = Number(eleminateCurrency(buildFromMyShopComponentsPricing?.customCost)) || 0;
    const automaticMainCalculated = Number(pricing?.sum?.price) || 0;
    const customMainCalculated =
      Number(
        eleminateCurrency(
          buildFromMyShopComponentsPricing?.customCustomerFacingPrice || enterTotalPrice
        )
      ) || 0;
    const mainCalculated =
      pricingType === 'custom' ? customMainCalculated : automaticMainCalculated;
    const mainCost =
      pricingType === 'custom' ? customQuantity * customCost : Number(pricing?.sum?.cost) || 0;
    const mainCustomerFacing =
      Number(
        eleminateCurrency(
          pricingType === 'custom'
            ? buildFromMyShopComponentsPricing?.customCustomerFacingPrice
            : buildFromMyShopComponentsPricing?.customerFacingPrice
        )
      ) || mainCalculated;
    const shippingCalculated = buildFromMyShopComponentsShipping?.active
      ? Number(
          eleminateCurrency(buildFromMyShopComponentsShipping?.price || shippingPrice?.price)
        ) || 0
      : 0;
    const shippingCost = buildFromMyShopComponentsShipping?.active
      ? Number(eleminateCurrency(buildFromMyShopComponentsShipping?.cost || shippingPrice?.cost)) ||
        0
      : 0;
    const shippingCustomerFacing = buildFromMyShopComponentsShipping?.active
      ? Number(
          eleminateCurrency(
            buildFromMyShopComponentsShipping?.customerFacingPrice ||
              buildFromMyShopComponentsShipping?.price
          )
        ) || shippingCalculated
      : 0;

    return {
      totalCost: mainCost + shippingCost,
      totalCalculated: mainCalculated + shippingCalculated,
      totalCustomerFacing: mainCustomerFacing + shippingCustomerFacing,
    };
  }, [
    buildFromMyShopComponentsPricing,
    buildFromMyShopComponentsShipping,
    pricing,
    pricingType,
    enterTotalPrice,
    shippingPrice,
  ]);

  useEffect(() => {
    if (hasCustomerFacingDescription) return;

    const automaticCustomerFacing =
      Number(pricing?.sum?.price) > 0 ? `$${Number(pricing.sum.price).toFixed(2)}` : '';
    const customCustomerFacing =
      Number(enterTotalPrice) > 0 ? `$${Number(enterTotalPrice).toFixed(2)}` : '';

    setBuildFromMyShopComponentsPricing((prev) => {
      const next = {
        ...prev,
        customerFacingPrice: automaticCustomerFacing,
        customCustomerFacingPrice: customCustomerFacing,
      };

      if (
        prev.customerFacingPrice === next.customerFacingPrice &&
        prev.customCustomerFacingPrice === next.customCustomerFacingPrice
      ) {
        return prev;
      }

      return next;
    });
  }, [hasCustomerFacingDescription, pricing?.sum?.price, enterTotalPrice]);

  useEffect(() => {
    if (!shouldAutoRecalculateBuildPricingRef.current) return;

    setBuildFromMyShopComponentsPricing((prev) => {
      return {
        ...prev,
        customerFacingPrice: pricing?.sum?.price ? `$${pricing?.sum?.price?.toFixed(2)}` : '',
      };
    });
  }, [pricing?.sum?.price]);

  const additionalAdj = useMemo(() => {
    return {
      shipping: {
        active: isLocked
          ? buildFromMyShopComponentsShipping?.clientPriceAdjustmentType
          : additionalPriceAdjustment,
        value: isLocked
          ? buildFromMyShopComponentsShipping.clientPriceAdjustment
          : additionalPriceAdjustmentValue,
      },
      pricing: {
        active: isLocked
          ? buildFromMyShopComponentsPricing?.type === 'automatic'
            ? buildFromMyShopComponentsPricing?.clientPriceAdjustmentType
            : buildFromMyShopComponentsPricing?.customClientPriceAdjustmentType
          : additionalPriceAdjustment,
        value: isLocked
          ? buildFromMyShopComponentsPricing?.type === 'automatic'
            ? buildFromMyShopComponentsPricing.clientPriceAdjustment
            : buildFromMyShopComponentsPricing.customClientPriceAdjustment
          : additionalPriceAdjustmentValue,
      },
    };
  }, [
    buildFromMyShopComponentsShipping,
    additionalPriceAdjustment,
    additionalPriceAdjustmentValue,
    buildFromMyShopComponentsPricing,
    isLocked,
  ]);

  const myShopBuildDate = new Date('2026-05-12');
  const thirtyDaysAfterMyShopBuildRelease = new Date(myShopBuildDate);
  thirtyDaysAfterMyShopBuildRelease.setDate(thirtyDaysAfterMyShopBuildRelease.getDate() + 30);
  const showBuildMyShopNewPill = thirtyDaysAfterMyShopBuildRelease > new Date();

  const documentControlsDisabled = loading || disabled || duplicate || !existingId;
  const headerDocumentPills = (
    <div className="flex flex-wrap items-center gap-2">
      <HeaderDocumentPill
        label="Estimate"
        status={documentState.estimateStatus}
        statusOptions={ESTIMATE_STATUS_OPTIONS}
        statusClassName={getEstimateStatusClass}
        number={documentState.estimateNumber}
        date={documentState.estimateDateSent}
        disabled={documentControlsDisabled}
        dataQaPrefix="purchased_goods_estimate"
        onStatusChange={(value) => handleDocumentFieldChange('estimateStatus', value)}
        onNumberChange={(value) => handleDocumentFieldChange('estimateNumber', value)}
        onDateChange={(value) => handleDocumentFieldChange('estimateDateSent', value)}
      />
      <HeaderDocumentPill
        label="Invoice"
        status={documentState.invoiceStatus}
        statusOptions={INVOICE_STATUS_OPTIONS}
        statusClassName={getInvoiceStatusClass}
        number={documentState.invoiceNumber}
        date={documentState.invoiceDateSent}
        disabled={documentControlsDisabled}
        dataQaPrefix="purchased_goods_invoice"
        onStatusChange={(value) => handleDocumentFieldChange('invoiceStatus', value)}
        onNumberChange={(value) => handleDocumentFieldChange('invoiceNumber', value)}
        onDateChange={(value) => handleDocumentFieldChange('invoiceDateSent', value)}
      />
      <HeaderDocumentPill
        label="PO"
        status={documentState.poStatus}
        statusOptions={PO_STATUS_OPTIONS}
        statusClassName={getPoStatusClass}
        number={documentState.poNumber}
        date={documentState.poDate}
        disabled={documentControlsDisabled}
        dataQaPrefix="purchased_goods_po"
        onStatusChange={(value) => handleDocumentFieldChange('poStatus', value)}
        onNumberChange={(value) => handleDocumentFieldChange('poNumber', value)}
        onDateChange={(value) => handleDocumentFieldChange('poDate', value)}
      />
    </div>
  );

  const hasItemWithSpecifyPrice = buildFromMyShopComponents?.find(
    (i) => !resolveItemBillingTypeClientBased(form?.client, i?.item)?.method?.includes('markUp')
  );

  if (account && !canAccessPurchasedGoods) {
    return null;
  }

  return (
    <>
      <BottomSheet {...bottom_sheet_props} headerExtra={headerDocumentPills}>
        <div className="space-y-4 p-4 bg-muted-50 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground text-base">Basic Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Combobox
                  id="client"
                  disabled={pricingFieldsDisabled}
                  searchPlaceholder={'Type to search client'}
                  actionLabel={'Add New Client'}
                  type={'action'}
                  placeholder={'Select or add client'}
                  onItemChoose={(item) => handleOnChooseItem('client', item)}
                  options={flattenedClients}
                  value={form?.client?.displayName || ''}
                  onAction={() => handleShowWideSheet(true)}
                  error={formErrors?.client?.active}
                />
                {formErrors?.client?.active && (
                  <span className="text-sm text-error">{formErrors?.client?.message}</span>
                )}
              </div>
              <div className="space-y-2">
                <Label>Room *</Label>
                <Combobox
                  id="room"
                  disabled={pricingFieldsDisabled || !form.client}
                  searchPlaceholder={'Type to search or create new room'}
                  actionLabel={'Add New Room to Client'}
                  type={'create'}
                  placeholder={'Select or add room'}
                  onItemChoose={(item) => handleOnChooseItem('room', item)}
                  options={rooms}
                  value={form?.room?.name || ''}
                  onAction={(value) => handleCreateNewRoom(value)}
                  error={formErrors?.room?.active}
                />
                {formErrors?.room?.active && (
                  <span className="text-sm text-error">{formErrors?.room?.message}</span>
                )}
              </div>
              <div className="space-y-2">
                <Label>Location / ID</Label>
                <Input
                  id="window_location_id"
                  data-qa="window_location_id"
                  name="window_location_id"
                  value={form.window_location_id || ''}
                  disabled={pricingFieldsDisabled}
                  onChange={(e) => handleOnChangeInput(e)}
                  placeholder="Enter window name, location, or ID"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover hover-visible:outline-none hover-visible:ring-2 hover-visible:ring-ring hover-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Combobox
                  id="itemType"
                  disabled={pricingFieldsDisabled}
                  searchPlaceholder={'Type to search'}
                  actionLabel={''}
                  placeholder={'Select category'}
                  onItemChoose={(item) => handleOnChooseItem('itemType', item)}
                  options={itemTypes}
                  value={form?.itemType?.name || ''}
                  error={formErrors?.itemType?.active}
                />
                {formErrors?.itemType?.active && (
                  <span className="text-sm text-error">{formErrors?.itemType?.message}</span>
                )}
              </div>
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Combobox
                  id="supplier"
                  disabled={pricingFieldsDisabled}
                  searchPlaceholder={'Type to search or create new supplier'}
                  actionLabel={'Save to My Shop'}
                  subActionLabel={'Add to Order'}
                  type={'create'}
                  placeholder={'Type to search or create new supplier'}
                  onItemChoose={(item) => handleOnChooseItem('supplier', item)}
                  options={suppliers}
                  value={form?.supplier?.displayName || ''}
                  onAction={(value) => handleCreateSupplier(value)}
                  onSubAction={(value) => handleCreateSupplier(value)}
                />
                <p className="text-xs text-muted-foreground">
                  Required to save components to My Shop
                </p>
              </div>
              <div className="space-y-2">
                <Label>Purchaser</Label>
                <RadioGroup
                  value={whoWillOrder}
                  onValueChange={(value) => {
                    if (pricingFieldsDisabled) return;
                    setWhoWillOrder(value);
                  }}
                  aria-readonly={pricingFieldsDisabled}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled={pricingFieldsDisabled}
                      aria-readonly={pricingFieldsDisabled}
                      value="workroom"
                      id="purchaser-workroom"
                    />
                    <Label htmlFor="purchaser-workroom">Workroom</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      disabled={pricingFieldsDisabled}
                      aria-readonly={pricingFieldsDisabled}
                      value="com"
                      id="purchaser-client"
                    />
                    <Label htmlFor="purchaser-client">Client (COM)</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <Label>Upload a PO & Other Files</Label>

                <div
                  onDrop={handleDragDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className="border-2 mt-[8px] border-input border-dashed rounded-lg p-6"
                >
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*,application/pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleChangeFile(e)}
                  />

                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="upload_icon">
                      <UploadIcon className="" width={30} height={30} />
                    </div>

                    <span draggable={false} className="text-sm text-muted-foreground">
                      Drag and drop files here or click to browse
                    </span>

                    <span
                      style={{
                        pointerEvents: dragActive ? 'none' : 'all',
                        userSelect: 'none',
                      }}
                      draggable={false}
                      onClick={() => {
                        if (loading || disabled) return;
                        handleChooseFile();
                      }}
                      className={`cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 from_computer ${
                        false ? 'disabled' : ''
                      }`}
                    >
                      Select File
                    </span>

                    {uploadError && (
                      <div
                        draggable={false}
                        style={{
                          marginTop: '12px',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          width: '100%',
                        }}
                      >
                        <UploadErrorBanner centered message={uploadError} />
                      </div>
                    )}
                  </div>

                  {imageUploads?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">
                        {existingId
                          ? 'Uploaded Files'
                          : 'Pending Upload (will be uploaded on save)'}
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {imageUploads?.map((u, idx) => {
                          const url = u.fromRest
                            ? encodeURI(`${Config.fileStoreUrl}${u.source}`)
                            : u.source;
                          const uploadModified = {
                            ...u,
                            index: idx,
                            name: u.name,
                          };
                          const isPdf = isPdfUpload(uploadModified);
                          const displayUrl = isPdf
                            ? resolvePdfDisplayUrl(uploadModified, url)
                            : url;

                          return (
                            <div key={idx} className="relative group">
                              <div className="aspect-square rounded border overflow-hidden bg-muted border-input border-dashed">
                                {isPdf ? (
                                  displayUrl ? (
                                    <PdfPreview key={idx} id={idx} file={u} />
                                  ) : (
                                    <img
                                      className="w-full h-full object-cover"
                                      draggable={false}
                                      src={DefaultImageTest}
                                    />
                                  )
                                ) : (
                                  <img
                                    className="w-full h-full object-cover cursor-pointer"
                                    draggable={false}
                                    src={url || DefaultImageTest}
                                    onClick={() => {
                                      window.open(openImagePreview(u), '_blank');
                                    }}
                                  />
                                )}
                              </div>

                              <button
                                onClick={() => handleDeleteUploadedImage(u)}
                                type="button"
                                disabled={nonPricingDisabled}
                                className="hover_danger inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 hover:bg-accent rounded-md absolute top-1 right-1 h-6 w-6 p-0 bg-background/80 text-muted-foreground hover:text-destructive"
                              >
                                <TrashIcon />
                              </button>

                              <button
                                onClick={() => window.open(openImagePreview(u), '_blank')}
                                type="button"
                                disabled={nonPricingDisabled}
                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 hover:bg-accent rounded-md absolute top-1 right-[30px] h-6 w-6 p-0 bg-background/80 text-muted-foreground hover:text-primary"
                              >
                                <ShareIcon className="cursor-pointer" width={20} height={20} />
                              </button>

                              <span className="block text-xs truncate mt-1">
                                <a rel="noopener" href={openImagePreview(u)} target="_blank">
                                  {u?.name || ''}
                                </a>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  disabled={nonPricingDisabled}
                  value={form?.notes}
                  name="notes"
                  onChange={(e) => handleOnChangeInput(e)}
                  placeholder="Enter internal notes"
                  className="min-h-[120px] flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Section 2: Components (with table) */}
        <div className="space-y-4 p-4 bg-muted-50 rounded-lg">
          <div className="flex items-start md:items-center flex-col md:flex-row gap-2">
            <h3 className="font-semibold text-foreground text-base">Components Needed</h3>

            <div className="w-full md:w-max text-muted-foreground">
              <RadioGroup
                value={componentsType}
                onValueChange={(value) => {
                  if (pricingFieldsDisabled) return;
                  setComponentsType(value);
                }}
                className="flex items-center gap-0 rounded-md bg-accent p-1"
              >
                <div
                  className={`w-full md:w-max ${
                    componentsType === 'components-build' ? 'rounded-sm bg-white' : ''
                  }`}
                >
                  <RadioGroupItem
                    className="hidden"
                    id="components-build"
                    value="components-build"
                    disabled={pricingFieldsDisabled}
                  />
                  <Label
                    className={`h-[32px] text-sm font-bold justify-center md:justify-start px-[12px] py-[6px] ${
                      componentsType === 'components-build' ? 'text-foreground' : ''
                    } ${pricingFieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    htmlFor="components-build"
                  >
                    Build from My Shop
                    {showBuildMyShopNewPill && <span className="new_pill">NEW</span>}
                  </Label>
                </div>

                <div
                  className={`w-full md:w-max ${
                    componentsType === 'components-total' ? 'rounded-sm bg-white' : ''
                  }`}
                >
                  <RadioGroupItem
                    className="hidden"
                    id="components-total"
                    value="components-total"
                    disabled={pricingFieldsDisabled}
                  />
                  <Label
                    className={`h-[32px] text-sm font-bold justify-center md:justify-start px-[12px] py-[6px] ${
                      componentsType === 'components-total' ? 'text-foreground' : ''
                    } ${pricingFieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    htmlFor="components-total"
                  >
                    {/* Enter Total Only */}
                    Enter Manually
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="border border-input rounded-lg overflow-hidden">
              <div className="relative overflow-x-auto scrollbar-visible">
                <div className="relative w-full overflow-auto">
                  {(!form.supplier || !form.itemType) && componentsType === 'components-build' ? (
                    <div className="text-center py-8 text-muted-foreground border-input rounded-lg bg-muted/30">
                      Select Category and Supplier above to start adding components
                    </div>
                  ) : (
                    <table
                      className={`w-full caption-bottom text-sm min-w-max ${componentsType === 'components-build' ? 'table-fixed' : ''}`}
                    >
                      <thead className="[&_tr]:border-b border-input sticky top-0 bg-background z-10">
                        <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/50">
                          {componentsType === 'components-build' ? (
                            <>
                              {isHardwareChosen && (
                                <th className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[12%]">
                                  <div>
                                    <span>Subcategory</span>
                                  </div>
                                </th>
                              )}
                              <th
                                className={`h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 ${isHardwareChosen ? 'w-[18%]' : 'w-[22%]'}`}
                              >
                                <div>
                                  <span>Ordering Description</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                <div>
                                  <span>Internal Note</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[50px]">
                                <div>
                                  <span>URL</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-center w-[7%]">
                                <div>
                                  <span>Qty</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right w-[10%]">
                                <div>
                                  <span>Cost</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right w-[10%]">
                                <div>
                                  <span>Markup %</span>
                                </div>
                              </th>
                              {additionalPriceAdjustment &&
                                additionalPriceAdjustment !== 'none' && (
                                  <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right align-middle w-[10%]">
                                    <div>
                                      <span>Client Price Adjustment</span>
                                    </div>
                                  </th>
                                )}
                              <th className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right w-[10%]">
                                <div>
                                  <span>Price</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                <div>
                                  <span>PO Note to Supplier</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[50px] text-center align-middle">
                                <div></div>
                              </th>
                            </>
                          ) : (
                            <>
                              <th className="h-12 px-1.5 text-left font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[400px] align-middle">
                                <div>
                                  <span>Estimate Description</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[150px] text-center align-middle">
                                <div>
                                  <span>Qty</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[200px] text-right align-middle">
                                <div>
                                  <span>Cost</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[200px] text-right align-middle">
                                <div>
                                  <span>Product Markup %</span>
                                </div>
                              </th>
                              {additionalPriceAdjustment &&
                                additionalPriceAdjustment !== 'none' && (
                                  <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[200px] text-right align-middle">
                                    <div>
                                      <span>Client Price Adjustment</span>
                                    </div>
                                  </th>
                                )}
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[200px] text-right align-middle">
                                <div>
                                  <span>Calculated Price</span>
                                </div>
                              </th>
                              <th className="h-12 px-1.5 font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 w-[200px] text-right align-middle">
                                <div>
                                  <span>Customer-Facing Price</span>
                                </div>
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>

                      <tbody className="[&_tr:last-child]:border-0">
                        {componentsType === 'components-build' ? (
                          <>
                            {/* BUILD COMPONENTS */}
                            {buildFromMyShopComponents?.map((i: any, index: number) => {
                              const filteredMyShopItems = myShopItems?.filter((f: any) => {
                                let isSubCategoryChosen = !!i?.category;

                                let matches_by_item_type = form?.itemType?.id === f?.categoryId;
                                let matches_by_supplier = form?.supplier?.id === f?.supplierId;
                                let matches_by_sub_category = f.subcategoryId === i?.category?.id;

                                if (!matches_by_item_type || !matches_by_supplier) {
                                  return false;
                                }

                                return isSubCategoryChosen ? matches_by_sub_category : true;
                              });

                              console.log(
                                'test ---1',
                                resolveItemBillingTypeClientBased(form?.client, i?.item)?.value
                              );

                              return (
                                <tr
                                  key={index}
                                  className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-muted/50 hover:bg-muted/70 min-h-[80px]"
                                >
                                  {isHardwareChosen && (
                                    <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 min-w-[300px] max-w-[300px] align-middle py-3">
                                      <div>
                                        <Combobox
                                          id="category"
                                          disabled={pricingFieldsDisabled}
                                          searchPlaceholder={'Select subcategory'}
                                          actionLabel={''}
                                          placeholder={'Select subcategory'}
                                          onItemChoose={(item) =>
                                            handleSelectBuildItem({
                                              type: 'components-build',
                                              key: 'category',
                                              component: i,
                                              item,
                                            })
                                          }
                                          options={subCategories}
                                          value={i?.category?.name || ''}
                                          error={formErrors?.itemType?.active}
                                        />
                                      </div>
                                    </td>
                                  )}
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 min-w-[300px] max-w-[300px] align-middle py-3">
                                    <div>
                                      <Combobox
                                        disabled={pricingFieldsDisabled}
                                        searchPlaceholder={'Search by description or item #'}
                                        actionLabel={'Add New Component'}
                                        type={'action'}
                                        placeholder={'Search by description or item #'}
                                        onItemChoose={(item) =>
                                          handleSelectBuildItem({
                                            type: 'components-build',
                                            key: 'item',
                                            component: i,
                                            item,
                                          })
                                        }
                                        options={filteredMyShopItems}
                                        value={i?.item?.name || ''}
                                        onAction={(itemName) =>
                                          handleCreateNewMyShopItem(itemName, i)
                                        }
                                        myShopItems={!!i?.item}
                                        myShopItemsAction={() => handleOpenMyShopItemModal(i)}
                                        twoRowDisplay
                                      />
                                      {componentRowErrors[getComponentKey(i, index)] && (
                                        <span className="mt-1 block text-xs text-error">
                                          {componentRowErrors[getComponentKey(i, index)]}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    {i?.item?.notes ? (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <div className="max-h-[56px] h-full text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors overflow-hidden whitespace-pre-line">
                                            <span className="line-clamp-2">
                                              {i?.item?.notes || ''}
                                            </span>
                                          </div>
                                        </PopoverTrigger>
                                        <PopoverContent
                                          side={'top'}
                                          className="wrap-text whitespace-pre-line max-h-48 overflow-auto text-sm text-muted-foreground w-72"
                                        >
                                          {i?.item?.notes || ''}
                                        </PopoverContent>
                                      </Popover>
                                    ) : (
                                      <span className="text-muted-foreground text-sm">—</span>
                                    )}
                                  </td>
                                  <td className="px-1.5 w-[50px]">
                                    {i?.item?.url ? (
                                      <a
                                        className="text-primary hover:text-primary/80 text-sm inline-flex items-center gap-1"
                                        href={normalizeUrl(i?.item?.url)}
                                        target="_blank"
                                      >
                                        Link
                                        <LucideExternalLink className="lucide lucide-external-link h-3.5 w-3.5" />
                                      </a>
                                    ) : (
                                      <span className="text-muted-foreground text-sm">—</span>
                                    )}
                                  </td>
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    <div className="w-full flex justify-center">
                                      <div className="min-w-[100px] max-w-[100px]">
                                        <Input
                                          value={i?.quantity || ''}
                                          name={'quantity'}
                                          onChange={(e) =>
                                            handleOnChangeBuiltComponents({
                                              name: e.target.name,
                                              value: e.target.value,
                                              item: i,
                                            })
                                          }
                                          data-qa={`${i?.id}-components-build-qty-input`}
                                          disabled={pricingFieldsDisabled}
                                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-center min-w-[50px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    {i?.item ? (
                                      <div className="wrap-text h-10 px-3 py-2 flex items-center justify-end text-sm bg-muted/30 rounded">
                                        {isLocked ? (
                                          valuteNumberFormatter(eleminateCurrency(i?.cost))
                                        ) : !resolveItemBillingTypeClientBased(
                                            form?.client,
                                            i?.item
                                          )?.method?.includes('markUp') || !i?.item?.cost ? (
                                          <Input
                                            value={i?.cost || ''}
                                            name={'cost'}
                                            onChange={(e) =>
                                              handleOnChangeBuiltComponents({
                                                name: e.target.name,
                                                value: e.target.value,
                                                item: i,
                                              })
                                            }
                                            onFocus={(e) =>
                                              handleOnFocusInputPrice(e, 'components', i)
                                            }
                                            onBlur={(e) =>
                                              handleOnBlurInputPrice(e, 'components', i)
                                            }
                                            placeholder="$0.00"
                                            data-qa={`${i?.id}-components-build-cost-input`}
                                            disabled={pricingFieldsDisabled}
                                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right"
                                          />
                                        ) : (
                                          valuteNumberFormatter(i?.item?.cost) || ''
                                        )}
                                      </div>
                                    ) : (
                                      <div className="min-w-[120px] px-3 py-2">
                                        <Input
                                          value={i?.cost || ''}
                                          name={'cost'}
                                          onChange={(e) =>
                                            handleOnChangeBuiltComponents({
                                              name: e.target.name,
                                              value: e.target.value,
                                              item: i,
                                            })
                                          }
                                          onFocus={(e) =>
                                            handleOnFocusInputPrice(e, 'components', i)
                                          }
                                          onBlur={(e) => handleOnBlurInputPrice(e, 'components', i)}
                                          placeholder="$0.00"
                                          data-qa={`${i?.id}-components-build-cost-input`}
                                          disabled={pricingFieldsDisabled}
                                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right"
                                        />
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    <div className="wrap-text px-3 py-2 min-w-[120px] h-10 flex items-center justify-end text-sm bg-muted/30 rounded">
                                      {!resolveItemBillingTypeClientBased(
                                        form?.client,
                                        i?.item
                                      )?.method?.includes('markUp')
                                        ? '—'
                                        : isLocked
                                          ? i?.markup
                                            ? `${i?.markup}%`
                                            : '—'
                                          : Number(
                                                resolveItemBillingTypeClientBased(
                                                  form?.client,
                                                  i?.item
                                                )?.value
                                              ) > 0
                                            ? `${resolveItemBillingTypeClientBased(form?.client, i?.item)?.value}%`
                                            : '—'}
                                    </div>
                                  </td>
                                  {additionalPriceAdjustment &&
                                    additionalPriceAdjustment !== 'none' && (
                                      <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3">
                                        <div className="wrap-text h-10 text-muted-foreground px-3 py-2 bg-muted/30 rounded-md flex items-center justify-end font-medium text-sm">
                                          <span>
                                            {additionalPriceAdjustment === 'discount' ? '-' : ''}
                                            {additionalPriceAdjustmentValue || '0.00'}%
                                          </span>
                                        </div>
                                      </td>
                                    )}
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    <div className="wrap-text h-10 px-3 py-2 flex items-center justify-end text-sm font-medium bg-muted/30 rounded">
                                      {pricingType === 'custom'
                                        ? ''
                                        : calculateComponentPrice(
                                              {
                                                ...i,
                                                cost: isLocked
                                                  ? eleminateCurrency(i?.cost)
                                                  : i?.item
                                                    ? !resolveItemBillingTypeClientBased(
                                                        form?.client,
                                                        i?.item
                                                      )?.method?.includes('markUp') ||
                                                      !i?.item?.cost
                                                      ? Number(eleminateCurrency(i?.cost)) ||
                                                        resolveItemBillingTypeClientBased(
                                                          form?.client,
                                                          i?.item
                                                        )?.value
                                                      : i?.item?.cost || ''
                                                    : eleminateCurrency(i?.cost),
                                                markup: !resolveItemBillingTypeClientBased(
                                                  form?.client,
                                                  i?.item
                                                )?.method?.includes('markUp')
                                                  ? ''
                                                  : isLocked
                                                    ? i?.markup
                                                    : resolveItemBillingTypeClientBased(
                                                        form?.client,
                                                        i?.item
                                                      )?.value,
                                              },
                                              {
                                                value: isLocked
                                                  ? i?.clientPriceAdjustment
                                                  : additionalPriceAdjustmentValue,
                                                type: isLocked
                                                  ? i?.clientPriceAdjustmentType
                                                  : additionalPriceAdjustment,
                                              }
                                            )
                                          ? valuteNumberFormatter(
                                              calculateComponentPrice(
                                                {
                                                  ...i,
                                                  cost: isLocked
                                                    ? eleminateCurrency(i?.cost)
                                                    : i?.item
                                                      ? !resolveItemBillingTypeClientBased(
                                                          form?.client,
                                                          i?.item
                                                        )?.method?.includes('markUp') ||
                                                        !i?.item?.cost
                                                        ? Number(eleminateCurrency(i?.cost)) ||
                                                          resolveItemBillingTypeClientBased(
                                                            form?.client,
                                                            i?.item
                                                          )?.value
                                                        : i?.item?.cost || ''
                                                      : eleminateCurrency(i?.cost),
                                                  markup: !resolveItemBillingTypeClientBased(
                                                    form?.client,
                                                    i?.item
                                                  )?.method?.includes('markUp')
                                                    ? ''
                                                    : isLocked
                                                      ? i?.markup
                                                      : resolveItemBillingTypeClientBased(
                                                          form?.client,
                                                          i?.item
                                                        )?.value,
                                                },
                                                {
                                                  value: isLocked
                                                    ? i?.clientPriceAdjustment
                                                    : additionalPriceAdjustmentValue,
                                                  type: isLocked
                                                    ? i?.clientPriceAdjustmentType
                                                    : additionalPriceAdjustment,
                                                }
                                              )
                                            ) || ''
                                          : '—'}
                                    </div>
                                  </td>
                                  <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                    <div className="relative">
                                      <Textarea
                                        id={`textareaComponents_${index}`}
                                        value={i?.poNotes || ''}
                                        name="poNotes"
                                        onChange={(e) =>
                                          handleOnChangeBuiltComponents({
                                            name: e.target.name,
                                            value: e.target.value,
                                            item: i,
                                          })
                                        }
                                        onInput={(e) => resizeTextarea(e.currentTarget, 36, true)}
                                        onFocus={(e) => handleOnFocusTextarea(e, index)}
                                        onBlur={(e) => handleOnBlurTextarea(e, 36, 72, index)}
                                        disabled={nonPricingDisabled}
                                        placeholder="Enter notes..."
                                        style={{
                                          overflow: 'hidden',
                                          minHeight: '36px',
                                          height: '36px',
                                        }}
                                        className="flex w-full rounded-md border border-input px-3 py-2 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[36px] text-sm bg-background resize-none"
                                      />
                                      <span
                                        id={`ta-dots-${index}`}
                                        style={{ display: 'none' }}
                                        className="absolute bottom-[5px] right-[9px] text-xs text-muted-foreground bg-background px-0.5 pointer-events-none"
                                      >
                                        …
                                      </span>
                                    </div>
                                  </td>
                                  <td className="">
                                    <div className="flex h-8 items-center justify-center">
                                      <button
                                        disabled={pricingFieldsDisabled}
                                        className="hover_danger inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors hover-visible:outline-none hover-visible:ring-2 hover-visible:ring-ring hover-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 hover:bg-accent rounded-md p-0 text-muted-foreground"
                                        type="button"
                                        onClick={() =>
                                          handleComponentsBuildRows({
                                            type: 'delete',
                                            payload: i,
                                          })
                                        }
                                      >
                                        <TrashIcon />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}

                            <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted hover:bg-background">
                              <td
                                colSpan={buildFromMyShopColsTotal}
                                className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2"
                              >
                                <div className="flex items-center justify-between">
                                  <button
                                    disabled={pricingFieldsDisabled}
                                    onClick={() =>
                                      handleComponentsBuildRows({
                                        type: 'create',
                                      })
                                    }
                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors hover-visible:outline-none hover-visible:ring-2 hover-visible:ring-ring hover-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-9 rounded-md px-3 text-primary hover:text-primary/80"
                                    type="button"
                                  >
                                    <LucidePlus className="lucide lucide-plus h-4 w-4 mr-1" />
                                    Add Row
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {/* BUILD PRICING */}
                            <tr className="border-b transition-colors data-[state=selected]:bg-muted bg-muted/30 border-t font-semibold hover:bg-muted/30">
                              <td
                                colSpan={isHardwareChosen ? 4 : 3}
                                className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 align-top"
                              >
                                <div className="space-y-2">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-muted-foreground">
                                      Pricing:
                                    </span>
                                    <div className="w-full md:w-max text-muted-foreground">
                                      <RadioGroup
                                        value={pricingType}
                                        onValueChange={(value) => {
                                          if (pricingFieldsDisabled) return;
                                          setPricingType(value);
                                        }}
                                        className="flex items-center gap-0 rounded-md bg-accent p-1"
                                      >
                                        <div
                                          className={`w-full md:w-max ${
                                            pricingType === 'automatic' ? 'rounded-sm bg-white' : ''
                                          }`}
                                        >
                                          <RadioGroupItem
                                            className="hidden"
                                            id="automatic"
                                            value="automatic"
                                            disabled={pricingFieldsDisabled}
                                          />
                                          <Label
                                            className={`h-[32px] text-[12px] font-bold justify-center md:justify-start px-[12px] py-[6px] ${
                                              pricingType === 'automatic' ? 'text-foreground' : ''
                                            } ${pricingFieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                            htmlFor="automatic"
                                          >
                                            Sum from components
                                          </Label>
                                        </div>

                                        <div
                                          className={`w-full md:w-max ${
                                            pricingType === 'custom' ? 'rounded-sm bg-white' : ''
                                          }`}
                                        >
                                          <RadioGroupItem
                                            className="hidden"
                                            id="custom"
                                            value="custom"
                                            disabled={pricingFieldsDisabled}
                                          />
                                          <Label
                                            className={`h-[32px] text-[12px] font-bold justify-center md:justify-start px-[12px] py-[6px] ${
                                              pricingType === 'custom' ? 'text-foreground' : ''
                                            } ${pricingFieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                            htmlFor="custom"
                                          >
                                            Enter total
                                          </Label>
                                        </div>
                                      </RadioGroup>
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1">
                                      <Label className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-xs text-muted-foreground font-normal">
                                        Customer-Facing Description
                                      </Label>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <LucideInfo className="lucide lucide-info h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-white text-popover-foreground z-[99999990] max-w-[320px]">
                                          <span className="leading-tight">
                                            If Customer-Facing Description is provided, only the
                                            total client-facing price with this description is
                                            utilized in estimates. Otherwise, the individual sales
                                            descriptions of each component will be used.
                                          </span>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                    <Textarea
                                      id={`alternateSalesDescription`}
                                      name="alternateSalesDescription"
                                      value={alternateSalesDescription}
                                      onChange={(e) => {
                                        if (pricingFieldsDisabled) return;
                                        setAlternateSalesDescription(e.target.value);
                                      }}
                                      onInput={(e) => resizeTextarea(e.currentTarget, 40, true)}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="Enter customer-facing description"
                                      style={{
                                        overflow: 'hidden',
                                        minHeight: '40px',
                                        height: '40px',
                                      }}
                                      className="flex w-full rounded-md border border-input px-3 py-2 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[40px] text-sm bg-background resize-none font-normal"
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-center align-middle">
                                {pricingType === 'custom' ? (
                                  <div className="min-w-[100px] max-w-[100px]">
                                    <Input
                                      value={buildFromMyShopComponentsPricing?.customQuantity || ''}
                                      name={'customQuantity'}
                                      onChange={handleChangeEnterTotalPricing}
                                      data-qa={`pricing-build-qty-input`}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="1"
                                      className="min-h-[40px] file:text-foreground selection:bg-primary selection:text-primary-foreground min-w-0 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none focus-visible:border-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex w-full px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-center font-semibold bg-blue-50 rounded border border-blue-100 hover:border-blue-200"
                                    />
                                  </div>
                                ) : (
                                  <span className="text-sm font-semibold">
                                    {buildFromMyShopComponents?.length <= 1 ? (
                                      // buildFromMyShopComponents?.[0]?.quantity ||
                                      // buildFromMyShopComponents?.[0]?.qty
                                      '1'
                                    ) : (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex items-center justify-center gap-1">
                                            <span>1</span>
                                            <LucideInfo className="lucide lucide-info h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="leading-tight bg-white text-popover-foreground z-[99999990] max-w-[320px]">
                                          <span className="">
                                            Quantity defaults to 1 when multiple items are combined
                                            into a single summary line for QuickBooks export. If
                                            needed, include the full item count in the description.
                                          </span>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-right align-middle">
                                {pricingType === 'custom' ? (
                                  <Input
                                    id="customCost"
                                    name="customCost"
                                    value={buildFromMyShopComponentsPricing?.customCost || ''}
                                    onFocus={(e) => handleOnFocusInputPrice(e, 'pricing')}
                                    onBlur={(e) => handleOnBlurInputPrice(e, 'pricing')}
                                    onChange={handleChangeEnterTotalPricing}
                                    disabled={pricingFieldsDisabled}
                                    placeholder="$0.00"
                                    className="flex w-full px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right font-semibold bg-blue-50 rounded border border-blue-100 hover:border-blue-200"
                                  />
                                ) : (
                                  <span className="wrap-text px-3 py-2 text-sm font-semibold">
                                    {hasItemWithSpecifyPrice
                                      ? ''
                                      : pricing?.sum?.cost
                                        ? valuteNumberFormatter(pricing?.sum?.cost)
                                        : ''}
                                  </span>
                                )}
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-right align-middle">
                                {pricingType === 'custom' ? (
                                  <div className="relative">
                                    <Input
                                      id="customMarkup"
                                      name="customMarkup"
                                      value={buildFromMyShopComponentsPricing?.customMarkup || ''}
                                      onBlur={(e) => handleOnBlurInputPrice(e, 'pricing')}
                                      onChange={handleChangeEnterTotalPricing}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="0"
                                      className="flex w-full px-3 pr-6 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right font-semibold bg-blue-50 rounded border border-blue-100 hover:border-blue-200"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none text-xs">
                                      %
                                    </span>
                                  </div>
                                ) : (
                                  <span className="wrap-text px-3 py-2 text-sm font-semibold">
                                    {hasItemWithSpecifyPrice ? '' : `${pricing?.sum?.markup}%`}
                                  </span>
                                )}
                              </td>
                              {additionalAdj?.pricing?.active &&
                                additionalAdj?.pricing?.active !== 'none' && (
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-right align-middle">
                                    <div className="wrap-text h-10 text-muted-foreground px-3 py-2 bg-muted/30 rounded-md flex items-center justify-end font-medium text-sm">
                                      <span>
                                        {additionalAdj?.pricing?.active === 'discount' ? '-' : ''}
                                        {additionalAdj?.pricing?.value || '0.00'}%
                                      </span>
                                    </div>
                                  </td>
                                )}
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-right align-middle">
                                <span className="wrap-text px-3 py-2 text-sm font-semibold">
                                  {pricingType === 'custom'
                                    ? Number(enterTotalPrice) > 0
                                      ? valuteNumberFormatter(enterTotalPrice)
                                      : '—'
                                    : pricing?.sum?.price
                                      ? valuteNumberFormatter(pricing?.sum?.price)
                                      : '—'}
                                </span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 align-middle">
                                <div className="relative">
                                  <Label className="peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-xs text-muted-foreground font-normal absolute bottom-[100%] right-0">
                                    Customer-Facing Price
                                  </Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Input
                                          id={
                                            pricingType === 'custom'
                                              ? 'customCustomerFacingPrice'
                                              : 'customerFacingPrice'
                                          }
                                          name={
                                            pricingType === 'custom'
                                              ? 'customCustomerFacingPrice'
                                              : 'customerFacingPrice'
                                          }
                                          value={
                                            pricingType === 'custom'
                                              ? buildFromMyShopComponentsPricing?.customCustomerFacingPrice ||
                                                ''
                                              : buildFromMyShopComponentsPricing?.customerFacingPrice ||
                                                ''
                                          }
                                          onFocus={(e) => handleOnFocusInputPrice(e, 'pricing')}
                                          onBlur={(e) => handleOnBlurInputPrice(e, 'pricing')}
                                          onChange={handleChangeEnterTotalPricing}
                                          disabled={buildMainCustomerFacingPriceInputDisabled}
                                          placeholder="$0.00"
                                          className="flex w-full px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right font-semibold bg-blue-50 rounded border border-blue-100 hover:border-blue-200"
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    {!hasCustomerFacingDescription && (
                                      <TooltipContent className="bg-white text-popover-foreground z-[99999990] max-w-[360px]">
                                        <span className="leading-tight">
                                          Customer-Facing Price is only available when a
                                          Customer-Facing Description is entered. Add a description
                                          first if you want this item to export as one
                                          customer-facing line.
                                        </span>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </div>
                              </td>
                              <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3"></td>
                            </tr>

                            <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background border-t hover:bg-background">
                              <td
                                colSpan={buildFromMyShopColsTotal}
                                className="px-1.5 py-4 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pl-4"
                              >
                                <Label
                                  htmlFor="add-shipping-and-handling"
                                  className="w-max cursor-pointer"
                                >
                                  <Checkbox
                                    disabled={pricingFieldsDisabled}
                                    checked={buildFromMyShopComponentsShipping.active}
                                    onCheckedChange={() =>
                                      handleSelectShippingAndHandlingBuild(
                                        !buildFromMyShopComponentsShipping.active
                                      )
                                    }
                                    className="border-primary"
                                    id="add-shipping-and-handling"
                                  />
                                  Add Shipping & Handling
                                </Label>
                              </td>
                            </tr>

                            {buildFromMyShopComponentsShipping.active && (
                              <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background hover:bg-background">
                                <td
                                  colSpan={isHardwareChosen ? 4 : 3}
                                  className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2"
                                >
                                  <div>
                                    <Input
                                      id="name"
                                      name="name"
                                      value={buildFromMyShopComponentsShipping?.name || ''}
                                      onChange={handleSetShippingBuild}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="Shipping & Handling"
                                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm"
                                    />
                                  </div>
                                </td>
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                  <div className="h-10 px-3 py-2 flex items-center justify-center text-sm">
                                    1
                                  </div>
                                </td>
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                  <div>
                                    <Input
                                      id="cost"
                                      name="cost"
                                      value={buildFromMyShopComponentsShipping?.cost || ''}
                                      onFocus={(e) => handleOnFocusInputPrice(e, 'shipping')}
                                      onBlur={(e) => handleOnBlurInputPrice(e, 'shipping')}
                                      onChange={handleSetShippingBuild}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="$0.00"
                                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right"
                                    />
                                  </div>
                                </td>
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                  <div className="relative">
                                    <Input
                                      id="markup"
                                      name="markup"
                                      value={buildFromMyShopComponentsShipping?.markup || ''}
                                      onBlur={(e) => handleOnBlurInputPrice(e, 'shipping')}
                                      onChange={handleSetShippingBuild}
                                      disabled={pricingFieldsDisabled}
                                      placeholder="0"
                                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right pr-6"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none text-xs">
                                      %
                                    </span>
                                  </div>
                                </td>
                                {additionalAdj?.shipping?.active &&
                                  additionalAdj?.shipping?.active !== 'none' && (
                                    <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                      <div className="wrap-text h-10 text-muted-foreground px-3 py-2 flex items-center justify-end text-sm text-muted-foreground bg-muted/30 rounded">
                                        <span>
                                          {additionalAdj?.shipping?.active === 'discount'
                                            ? '-'
                                            : ''}
                                          {additionalAdj?.shipping?.value || '0.00'}%
                                        </span>
                                      </div>
                                    </td>
                                  )}
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                  <div className="wrap-text h-10 px-3 py-2 flex items-center justify-end font-medium text-sm bg-muted/30 rounded">
                                    {shippingPrice?.price
                                      ? valuteNumberFormatter(shippingPrice?.price)
                                      : '—'}
                                  </div>
                                </td>
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2">
                                  <Input
                                    id="shippingCustomerFacingPrice"
                                    name="customerFacingPrice"
                                    value={
                                      buildFromMyShopComponentsShipping?.customerFacingPrice || ''
                                    }
                                    onFocus={(e) => handleOnFocusInputPrice(e, 'shipping')}
                                    onBlur={(e) => handleOnBlurInputPrice(e, 'shipping')}
                                    onChange={handleSetShippingBuild}
                                    disabled={buildShippingCustomerFacingPriceInputDisabled}
                                    placeholder="$0.00"
                                    className="flex w-full px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm text-right font-semibold bg-blue-50 rounded border border-blue-100 hover:border-blue-200"
                                  />
                                </td>
                                <td className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-2"></td>
                              </tr>
                            )}

                            <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background border-t font-semibold hover:bg-background">
                              <td
                                colSpan={isHardwareChosen ? 4 : 3}
                                className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3"
                              >
                                <span>Total</span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-center">
                                <span>1</span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                <span>{valuteNumberFormatter(buildTotals.totalCost)}</span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                <span>
                                  {pricingType === 'custom'
                                    ? `${Number(buildFromMyShopComponentsPricing?.customMarkup || 0)}%`
                                    : `${pricing?.sum?.markup || 0}%`}
                                </span>
                              </td>
                              {additionalAdj?.pricing?.active &&
                                additionalAdj?.pricing?.active !== 'none' && (
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                    <span>
                                      {additionalAdj?.pricing?.active === 'discount' ? '-' : ''}
                                      {additionalAdj?.pricing?.value || '0.00'}%
                                    </span>
                                  </td>
                                )}
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                <span>{valuteNumberFormatter(buildTotals.totalCalculated)}</span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                <span>
                                  {valuteNumberFormatter(buildTotals.totalCustomerFacing)}
                                </span>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3"></td>
                            </tr>
                          </>
                        ) : (
                          <>
                            <tr className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/50">
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div>
                                  <Input
                                    disabled={pricingFieldsDisabled}
                                    placeholder="Enter Description"
                                    name="estimateDescription"
                                    value={componentsTotalForm.estimateDescription}
                                    onChange={(e) => handleComponentsTotalInput(e)}
                                    onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                    onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                    className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-background"
                                  />
                                </div>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div>
                                  <Input
                                    disabled={pricingFieldsDisabled}
                                    placeholder="1"
                                    name="qty"
                                    value={componentsTotalForm.qty}
                                    onChange={(e) => handleComponentsTotalInput(e)}
                                    onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                    onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                    className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-center bg-background"
                                  />
                                </div>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div>
                                  <Input
                                    disabled={pricingFieldsDisabled}
                                    placeholder="$0.00"
                                    name="cost"
                                    value={componentsTotalForm.cost}
                                    onChange={(e) => handleComponentsTotalInput(e)}
                                    onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                    onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                    className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right bg-background"
                                  />
                                </div>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div className="relative">
                                  <Input
                                    disabled={pricingFieldsDisabled}
                                    placeholder="0.00"
                                    name="markup"
                                    value={componentsTotalForm.markup}
                                    onChange={(e) => handleComponentsTotalInput(e)}
                                    onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                    onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                    className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-6 bg-background"
                                  />

                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                                    %
                                  </span>
                                </div>
                              </td>
                              {additionalPriceAdjustment &&
                                additionalPriceAdjustment !== 'none' && (
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div className="h-10 px-3 py-2 bg-muted/30 rounded-md flex items-center justify-end font-medium text-sm">
                                      <span>
                                        {additionalPriceAdjustment === 'discount' ? '-' : ''}
                                        {additionalPriceAdjustmentValue || '0.00'}%
                                      </span>
                                    </div>
                                  </td>
                                )}
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div className="h-10 px-3 py-2 bg-muted/30 rounded-md flex items-center justify-end font-medium text-sm">
                                  <span>${componentsTotalForm.calculatedPrice || '0.00'}</span>
                                </div>
                              </td>
                              <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                <div>
                                  <Input
                                    disabled={pricingFieldsDisabled}
                                    placeholder="$0.00"
                                    name="customerFacingPrice"
                                    value={componentsTotalForm.customerFacingPrice}
                                    onChange={(e) => handleComponentsTotalInput(e)}
                                    onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                    onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                    className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right bg-primary/5"
                                  />
                                </div>
                              </td>
                            </tr>

                            <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background border-t hover:bg-background">
                              <td
                                colSpan={7}
                                className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3"
                              >
                                <Label htmlFor="add-shipping-and-handling">
                                  <Checkbox
                                    disabled={pricingFieldsDisabled}
                                    checked={shippingAndHandling}
                                    onCheckedChange={() =>
                                      handleSelectShippingAndHandling(!shippingAndHandling)
                                    }
                                    className="border-primary"
                                    id="add-shipping-and-handling"
                                  />
                                  Add Shipping & Handling
                                </Label>
                              </td>
                            </tr>

                            {shippingAndHandling && (
                              <>
                                <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background hover:bg-background">
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div>
                                      <Input
                                        disabled={pricingFieldsDisabled}
                                        name="shippingAndHandlingDescription"
                                        value={componentsTotalForm.shippingAndHandlingDescription}
                                        onChange={(e) => handleComponentsTotalInput(e)}
                                        onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                        onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                        className="flex w-full rounded-md border border-input px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10 text-sm bg-background hover:border-input"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div className="h-10 px-3 py-2 flex items-center justify-center text-sm">
                                      <span>{componentsTotalForm.shippingAndHandlingQty || 1}</span>
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div>
                                      <Input
                                        disabled={pricingFieldsDisabled}
                                        name="shippingAndHandlingCost"
                                        value={componentsTotalForm.shippingAndHandlingCost}
                                        onChange={(e) => handleComponentsTotalInput(e)}
                                        onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                        onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                        className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right bg-background hover:border-input"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div className="relative">
                                      <Input
                                        disabled={pricingFieldsDisabled}
                                        name="shippingAndHandlingMarkup"
                                        value={componentsTotalForm.shippingAndHandlingMarkup}
                                        onChange={(e) => handleComponentsTotalInput(e)}
                                        onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                        onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                        className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right pr-6 bg-background hover:border-input"
                                      />

                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                                        %
                                      </span>
                                    </div>
                                  </td>
                                  {additionalPriceAdjustment &&
                                    additionalPriceAdjustment !== 'none' && (
                                      <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                        <div className="h-10 px-3 py-2 flex items-center justify-end font-medium text-sm">
                                          <span>
                                            {additionalPriceAdjustment === 'discount' ? '-' : ''}
                                            {additionalPriceAdjustmentValue || '0.00'}%
                                          </span>
                                        </div>
                                      </td>
                                    )}
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div className="h-10 px-3 py-2 flex items-center justify-end font-medium text-sm">
                                      <span>
                                        $
                                        {componentsTotalForm.shippingAndHandlingCalculatedPrice ||
                                          '0.00'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                    <div>
                                      <Input
                                        disabled={pricingFieldsDisabled}
                                        name="shippingAndHandlingCustomerFacingPrice"
                                        value={
                                          componentsTotalForm.shippingAndHandlingCustomerFacingPrice
                                        }
                                        onChange={(e) => handleComponentsTotalInput(e)}
                                        onBlur={(e) => handleComponentsTotalInputBlur(e)}
                                        onFocus={(e) => handleComponentsTotalInputFocus(e)}
                                        className="flex h-10 w-full rounded-md border border-input px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right bg-primary/5 hover:border-input"
                                      />
                                    </div>
                                  </td>
                                </tr>

                                <tr className="border-b border-input transition-colors data-[state=selected]:bg-muted bg-background border-t font-semibold hover:bg-background">
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3">
                                    <div>
                                      <span>Total</span>
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3">
                                    <div>
                                      <span></span>
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                    <div>
                                      <span>
                                        $
                                        {(
                                          Number(componentsTotalForm.qty || 0) *
                                            Number(
                                              eleminateCurrency(componentsTotalForm.cost) || 0
                                            ) +
                                          Number(
                                            componentsTotalForm.shippingAndHandlingQty ||
                                              (shippingAndHandling ? 1 : 0)
                                          ) *
                                            Number(
                                              eleminateCurrency(
                                                componentsTotalForm.shippingAndHandlingCost
                                              ) || 0
                                            )
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3 text-right">
                                    <div>
                                      <span>{calculateTotalPercentage() || '0.00'}%</span>
                                    </div>
                                  </td>
                                  {additionalPriceAdjustment &&
                                    additionalPriceAdjustment !== 'none' && (
                                      <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-top py-3">
                                        <div className="h-10 px-3 py-2 flex items-center justify-end font-medium text-sm">
                                          <span>
                                            {additionalPriceAdjustment === 'discount' ? '-' : ''}
                                            {additionalPriceAdjustmentValue || '0.00'}%
                                          </span>
                                        </div>
                                      </td>
                                    )}
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3">
                                    <div className="h-10 px-3 py-2 bg-background rounded-md flex items-center justify-end font-semibold text-sm">
                                      $
                                      {(
                                        Number(
                                          eleminateCurrency(componentsTotalForm.calculatedPrice)
                                        ) +
                                        Number(
                                          eleminateCurrency(
                                            componentsTotalForm.shippingAndHandlingCalculatedPrice
                                          )
                                        )
                                      ).toFixed(2)}
                                    </div>
                                  </td>
                                  <td className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 align-middle py-3">
                                    <div className="h-10 px-3 py-2 bg-background rounded-md flex items-center justify-end font-semibold text-sm">
                                      $
                                      {(
                                        Number(
                                          eleminateCurrency(componentsTotalForm.customerFacingPrice)
                                        ) +
                                        Number(
                                          eleminateCurrency(
                                            componentsTotalForm.shippingAndHandlingCustomerFacingPrice
                                          )
                                        )
                                      ).toFixed(2)}
                                    </div>
                                  </td>
                                </tr>
                              </>
                            )}
                          </>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </BottomSheet>

      <AlertDialog
        open={openItemSupplierChange}
        onOpenChange={(value) => {
          if (!value) {
            setOpenItemSupplierChange(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="flex flex-col space-y-2 text-center sm:text-left">
            <h2 className="text-lg font-semibold">Change Category ?</h2>
            <p className="text-sm text-muted-foreground">
              You have{' '}
              {buildFromMyShopComponents?.filter((f) => f?.item !== null || f?.category !== null)
                ?.length || 0}{' '}
              component row(s) entered. Changing the{' '}
              {pendingItemSupplierChange?.key === 'itemType'
                ? 'category'
                : pendingItemSupplierChange?.key === 'supplier'
                  ? 'supplier'
                  : ''}{' '}
              will clear them. If you switch back, they'll be restored.
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col text-center sm:text-left">
            <Button
              onClick={() => {
                setOpenItemSupplierChange(false);
                setTimeout(() => {
                  setPendingItemSupplierChange({ key: '', item: null });
                }, 300);
              }}
              variant={'outline'}
              type="button"
            >
              Cancel
            </Button>
            <Button variant={'default'} onClick={handleResetComponents} type="button">
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={unlockDialogOpen}
        onOpenChange={(value) => {
          if (!value) {
            setUnlockDialogOpen(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="flex flex-col space-y-2 text-center sm:text-left">
            <h2 className="text-lg font-semibold">Unlock pricing?</h2>
            <p className="text-sm text-muted-foreground">
              Unlocking allows edits and will recalculate the price if any component prices have
              changed.
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col text-center sm:text-left">
            <Button onClick={() => setUnlockDialogOpen(false)} variant={'outline'} type="button">
              Cancel
            </Button>
            <Button variant={'default'} onClick={handleConfirmUnlockPrice} type="button">
              Unlock
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StandardItemModal
        isModalOpen={editMyShopItem?.active}
        categories={categories}
        suppliers={suppliers}
        itemId={editMyShopItem?.item?.id}
        activeItemClassId={editMyShopItem?.item?.itemClassId || null}
        fromCalculator={true}
        onModalClose={handleMyShopModalClose}
        useWideSheet
        canEditMyShop={canEditMyShop}
        prefill={editMyShopItem?.prefill}
        lockPrefill={editMyShopItem?.lockPrefill}
      />

      <WideSheetQuickAddClient {...wide_sheet_quick_add_client_props} />

      <ConfirmModal {...confirmModalProps}>
        <span>Would you like to save your progress?</span>
      </ConfirmModal>

      <ActionModal {...actionModalProps}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col space-y-2 text-center sm:text-left">
            <div className="text-lg font-semibold">Please confirm the action</div>

            <div className="text-sm text-muted-foreground">
              Are you sure you want to delete this item?
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              onClick={() => handleCancelDelete()}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 mt-2 sm:mt-0"
              type="button"
              variant={'outline'}
            >
              Cancel
            </Button>
            <Button onClick={() => handleDelete()} type="button" variant={'delete'}>
              Delete
            </Button>
          </div>
        </div>
      </ActionModal>
    </>
  );
};

export default PurchasedGoods;
